
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { jsPDF } from 'jspdf';
import { getOrthoDatabase } from '../contexts/constants';
import { useLanguage } from '../contexts/LanguageContext';
import { usePatient } from '../contexts/PatientContext';
import { PremiumActionButton, PremiumEmptyState, PremiumPageShell, PremiumSectionCard } from './premium';
import { auth, db as firestoreDb } from '../firebase/client';
import { doc, getDoc } from 'firebase/firestore';
import { uploadPatientDataUrl, uploadPatientFile } from '../services/storage';
import {
  analyzeDisease,
  analyzeMedicalReport,
  generateMedicalReportDocument,
  type GeneratedMedicalReportDocument,
  type MedicalReportAnalysisResult,
  type MedicalReportDocumentType,
} from '../services/geminiService';
import {
  buildReportVerificationBundle,
  generateQrCodeDataUrl,
  saveGeneratedReportRecord,
} from '../services/reportVerification';
import {
  buildBannerConfig,
  buildDoctorProfilePayload,
  getBannerLayoutMetrics,
  resolveBannerBackdropPalette,
  resolveBannerTextColor,
  resolveBannerTonePalette,
  normalizeDoctorProfile,
} from '../utils/doctorProfile';
import {
  cleanMedicalMarkdownText,
  normalizeMedicalMarkdownInput,
  renderInlineMedicalMarkdown,
} from '../utils/medicalMarkdown';
import PatientFilePickerModal from './PatientFilePickerModal';
import { LocalPathology, ClinicalNote, type Patient, type PatientFile } from "../contexts/types";
import {
  UsageLimitError,
  type UsageMetric,
} from "../services/usageLimits";
import {
  Search, 
  BrainCircuit, 
  BookOpen, 
  Activity, 
  Stethoscope, 
  AlertTriangle, 
  ChevronRight, 
  Database,
  Sparkles,
  Loader2,
  Layers,
  ImageOff,
  Filter,
  Bone,
  Baby,
  Skull,
  Zap,
  X,
  Camera,
  Upload,
  Trash2,
  FilePlus,
  Check,
  FileText,
  ScanText,
  Pill,
  ClipboardList,
  Copy,
  Printer,
  Share2,
  FileType,
  Download,
  FolderSearch,
  User
} from 'lucide-react';

type ConsultModuleProps = {
  moduleView?: 'combined' | 'pathologies' | 'report_analysis';
};

type PathologyAiResult = {
  nome: string;
  classificacao?: PathologyFieldValue;
  exameFisico?: PathologyFieldValue;
  imagem?: PathologyFieldValue;
  conduta?: PathologyFieldValue;
  opme?: PathologyFieldValue;
  definicao?: PathologyFieldValue;
  etiologia?: PathologyFieldValue;
  tiposConduta?: PathologyFieldValue;
  tratamentoDetalhado?: PathologyFieldValue;
  prognostico?: PathologyFieldValue;
  reabilitacao?: PathologyFieldValue;
};

type PathologyFieldValue = string | string[] | Record<string, unknown> | null | undefined;

type PathologyPdfSource = {
  title: string;
  typeLabel: string;
  classification: string[];
  examPhysical: string[];
  conduct: string;
  materials: string[];
  evidenceSections: Array<{ title: string; value: string }>;
};

type ReportDocumentOption = {
  type: MedicalReportDocumentType;
  labelKey: string;
  icon: React.ComponentType<{ className?: string }>;
  featured?: boolean;
};

type PdfDoctorProfile = {
  name: string;
  headerTitlePrimary?: string;
  headerTitleSecondary?: string;
  bannerTagline?: string;
  bannerSupportText?: string;
  bannerAccentTone?: string;
  bannerTextColor?: string;
  bannerPrimaryColor?: string;
  bannerAccentColor?: string;
  bannerBackgroundStyle?: string;
  bannerBackgroundSpec?: string;
  bannerLayoutVariant?: string;
  bannerLayoutSpec?: string;
  bannerPaletteSpec?: string;
  bannerTypographySpec?: string;
  bannerDecorativeSpec?: string;
  headerName?: string;
  bannerMode?: string;
  bannerTemplateId?: string;
  bannerLayoutPreset?: string;
  bannerOffsetLeftX?: number;
  bannerOffsetLeftY?: number;
  bannerOffsetRightX?: number;
  bannerOffsetRightY?: number;
  crm: string;
  crmState: string;
  rqe?: string;
  specialty?: string;
  subspecialty?: string;
  hospital?: string;
  phone?: string;
  email?: string;
  website?: string;
  orthoId?: string;
  logo?: string;
  banner?: string;
  signature?: string;
};

type PdfBlock =
  | { kind: 'heading'; level: 1 | 2 | 3; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: string[] }
  | { kind: 'spacer' };

const getSafeFileName = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase();

const stripMarkdown = (text: string) =>
  text
    .replace(/```[\s\S]*?```/g, '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/^\s*[-*]\s+/gm, '- ')
    .trim();

const normalizePdfText = (value: string) =>
  value
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r/g, '')
    .replace(/\s+/g, ' ')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/__(.*?)__/g, '$1')
    .replace(/\*/g, '')
    .replace(/`(.*?)`/g, '$1')
    .replace(/\[(.*?)\]\((.*?)\)/g, '$1')
    .replace(/<[^>]+>/g, ' ')
    .trim();

const normalizeComplementaryDocumentContent = (value: string) => {
  const text = String(value || '')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .replace(/\r/g, '')
    .trim();
  if (!text) return '';

  if (!/<\/?[a-z][\s\S]*>/i.test(text)) {
    return text;
  }

  return text
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|section|article|header|footer|blockquote)\s*>/gi, '\n\n')
    .replace(/<\s*\/?\s*h1\s*>/gi, '\n# ')
    .replace(/<\s*\/?\s*h2\s*>/gi, '\n## ')
    .replace(/<\s*\/?\s*h3\s*>/gi, '\n### ')
    .replace(/<\s*\/?\s*h4\s*>/gi, '\n#### ')
    .replace(/<\s*\/?\s*h5\s*>/gi, '\n##### ')
    .replace(/<\s*\/?\s*h6\s*>/gi, '\n###### ')
    .replace(/<\s*li\b[^>]*>/gi, '\n- ')
    .replace(/<\/\s*li\s*>/gi, '\n')
    .replace(/<\s*ul\b[^>]*>/gi, '\n')
    .replace(/<\/\s*ul\s*>/gi, '\n')
    .replace(/<\s*ol\b[^>]*>/gi, '\n')
    .replace(/<\/\s*ol\s*>/gi, '\n')
    .replace(/<\s*strong\b[^>]*>/gi, '**')
    .replace(/<\/\s*strong\s*>/gi, '**')
    .replace(/<\s*b\b[^>]*>/gi, '**')
    .replace(/<\/\s*b\s*>/gi, '**')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

const normalizePdfKey = (value: string) =>
  normalizePdfText(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

type PathologyCardTone = 'sky' | 'amber' | 'violet' | 'emerald' | 'yellow' | 'cyan' | 'rose' | 'indigo';

const PATHOLOGY_TONE_STYLES: Record<PathologyCardTone, { border: string; glow: string; icon: string; title: string }> = {
  sky: { border: 'border-sky-500/25', glow: 'bg-sky-500/10', icon: 'text-sky-300', title: 'text-sky-100' },
  amber: { border: 'border-amber-500/25', glow: 'bg-amber-500/10', icon: 'text-amber-300', title: 'text-amber-100' },
  violet: { border: 'border-violet-500/25', glow: 'bg-violet-500/10', icon: 'text-violet-300', title: 'text-violet-100' },
  emerald: { border: 'border-emerald-500/25', glow: 'bg-emerald-500/10', icon: 'text-emerald-300', title: 'text-emerald-100' },
  yellow: { border: 'border-yellow-500/25', glow: 'bg-yellow-500/10', icon: 'text-yellow-300', title: 'text-yellow-100' },
  cyan: { border: 'border-cyan-500/25', glow: 'bg-cyan-500/10', icon: 'text-cyan-300', title: 'text-cyan-100' },
  rose: { border: 'border-rose-500/25', glow: 'bg-rose-500/10', icon: 'text-rose-300', title: 'text-rose-100' },
  indigo: { border: 'border-indigo-500/25', glow: 'bg-indigo-500/10', icon: 'text-indigo-300', title: 'text-indigo-100' },
};

type PathologyInfoCardProps = {
  title: string;
  value: PathologyFieldValue;
  icon: React.ReactNode;
  tone: PathologyCardTone;
};

const PathologyInfoCard: React.FC<PathologyInfoCardProps> = ({ title, value, icon, tone }) => {
  const styles = PATHOLOGY_TONE_STYLES[tone];
  const normalizedText = cleanMedicalMarkdownText(value);

  if (!normalizedText) return null;

  return (
    <section className={`relative overflow-hidden rounded-2xl border ${styles.border} bg-slate-900/80 p-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]`}>
      <div className={`absolute inset-x-0 top-0 h-px ${styles.glow} opacity-90`} />
      <div className={`absolute right-0 top-0 h-20 w-20 rounded-full ${styles.glow} blur-3xl opacity-70`} />
      <header className="relative mb-4 flex items-start gap-3">
        <div className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border ${styles.border} ${styles.glow}`}>
          <span className={styles.icon}>{icon}</span>
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-[0.28em] text-slate-500">{title}</p>
          <div className={`mt-1 h-px w-12 ${styles.glow}`} />
        </div>
      </header>
      <div className={`space-y-2 text-sm leading-7 text-slate-300 ${styles.title}`}>
        {renderInlineMedicalMarkdown(normalizedText, 'text-sm leading-7 text-slate-300')}
      </div>
    </section>
  );
};

const pathologyText = (value: PathologyFieldValue) => cleanMedicalMarkdownText(value);
const pathologyLines = (value: PathologyFieldValue) =>
  pathologyText(value)
    .split('\n')
    .map((item) => item.trim())
    .filter(Boolean);

const parseMarkdownForPdf = (markdown: string): PdfBlock[] => {
  const blocks: PdfBlock[] = [];
  const lines = String(markdown || '')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\n/g, '\n')
    .split(/\r?\n/);
  let paragraphParts: string[] = [];
  let listBlock: { ordered: boolean; items: string[] } | null = null;
  let pendingSpacer = false;

  const flushParagraph = () => {
    if (paragraphParts.length > 0) {
      blocks.push({ kind: 'paragraph', text: normalizePdfText(paragraphParts.join(' ')) });
      paragraphParts = [];
    }
  };

  const flushList = () => {
    if (listBlock) {
      blocks.push({ kind: 'list', ordered: listBlock.ordered, items: listBlock.items });
      listBlock = null;
    }
  };

  const pushSpacer = () => {
    if (blocks.length > 0 && blocks[blocks.length - 1].kind !== 'spacer') {
      blocks.push({ kind: 'spacer' });
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) {
      flushParagraph();
      flushList();
      pendingSpacer = true;
      continue;
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      if (pendingSpacer) pushSpacer();
      pendingSpacer = false;
      const level = headingMatch[1].length as 1 | 2 | 3;
      blocks.push({ kind: 'heading', level, text: normalizePdfText(headingMatch[2]) });
      continue;
    }

    const orderedMatch = line.match(/^(\d+)\.\s+(.*)$/);
    const bulletMatch = line.match(/^[-*]\s+(.*)$/);
    if (orderedMatch || bulletMatch) {
      flushParagraph();
      if (pendingSpacer) pushSpacer();
      pendingSpacer = false;
      const ordered = !!orderedMatch;
      if (!listBlock || listBlock.ordered !== ordered) {
        flushList();
        listBlock = { ordered, items: [] };
      }
      listBlock.items.push(normalizePdfText((orderedMatch ? orderedMatch[2] : bulletMatch?.[1]) || ''));
      continue;
    }

    flushList();
    if (pendingSpacer) pushSpacer();
    pendingSpacer = false;
    paragraphParts.push(normalizePdfText(line));
  }

  flushParagraph();
  flushList();
  return blocks;
};

const sanitizePdfBlocks = (blocks: PdfBlock[], pageTitle: string) => {
  const metadataKeys = new Set([
    normalizePdfKey(pageTitle),
    normalizePdfKey('Paciente'),
    normalizePdfKey('Médico responsável'),
    normalizePdfKey('Médico Responsável'),
    normalizePdfKey('Paciente vinculado'),
    normalizePdfKey('Identificação profissional'),
    normalizePdfKey('Identificação'),
  ]);

  const result: PdfBlock[] = [];
  let previousKey = '';

  for (const block of blocks) {
    if (block.kind === 'spacer') {
      if (result.length > 0 && result[result.length - 1].kind !== 'spacer') {
        result.push(block);
      }
      continue;
    }

    const key =
      block.kind === 'heading' || block.kind === 'paragraph'
        ? normalizePdfKey(block.text)
        : block.kind === 'list'
          ? normalizePdfKey(block.items.join(' | '))
          : '';

    if (!key) continue;
    if (metadataKeys.has(key)) continue;
    if (key === previousKey) continue;

    if (block.kind === 'list') {
      const cleanedItems = block.items
        .map((item) => normalizePdfText(item))
        .filter(Boolean);
      if (!cleanedItems.length) continue;
      result.push({ ...block, items: cleanedItems });
    } else if (block.kind === 'heading' || block.kind === 'paragraph') {
      result.push({ ...block, text: normalizePdfText(block.text) });
    }

    previousKey = key;
  }

  return result;
};

const fetchImageAsDataUrl = async (src: string) => {
  try {
    if (/^data:/i.test(src)) {
      return src;
    }
    const image = new Image();
    image.crossOrigin = 'anonymous';

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Failed to load image from source'));
      image.src = src;
    });

    const width = image.naturalWidth || image.width || 1;
    const height = image.naturalHeight || image.height || 1;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return src;

    ctx.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL('image/png');
  } catch {
    return src;
  }
};

const isSvgDataUrl = (value: string) => /^data:image\/svg\+xml/i.test(value);

const rasterizeImageDataUrlToPng = async (dataUrl: string) => {
  try {
    const image = new Image();
    image.crossOrigin = 'anonymous';

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Failed to load image for rasterization'));
      image.src = dataUrl;
    });

    const width = image.naturalWidth || image.width || 1600;
    const height = image.naturalHeight || image.height || 280;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.clearRect(0, 0, width, height);
    ctx.drawImage(image, 0, 0, width, height);
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  }
};

const roundBannerCornersToPng = async (dataUrl: string, radius = 12) => {
  try {
    const image = new Image();
    image.crossOrigin = 'anonymous';

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Failed to load banner image for rounding'));
      image.src = dataUrl;
    });

    const width = image.naturalWidth || image.width || 1600;
    const height = image.naturalHeight || image.height || 280;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return dataUrl;

    const r = Math.max(0, Math.min(radius, Math.floor(Math.min(width, height) / 2)));
    ctx.clearRect(0, 0, width, height);
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.arcTo(width, 0, width, height, r);
    ctx.arcTo(width, height, 0, height, r);
    ctx.arcTo(0, height, 0, 0, r);
    ctx.arcTo(0, 0, width, 0, r);
    ctx.closePath();
    ctx.clip();
    ctx.drawImage(image, 0, 0, width, height);

    return canvas.toDataURL('image/png');
  } catch {
    return dataUrl;
  }
};

const renderOrthoHeaderToPng = async (
  backgroundDataUrl: string,
  bannerConfig: ReturnType<typeof buildBannerConfig>,
  width = 1600,
  height = 274
) => {
  try {
    const image = new Image();
    image.crossOrigin = 'anonymous';

    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error('Failed to load banner background'));
      image.src = backgroundDataUrl;
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return backgroundDataUrl;

    const r = Math.round(Math.min(width, height) * 0.02);
    ctx.clearRect(0, 0, width, height);
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.arcTo(width, 0, width, height, r);
    ctx.arcTo(width, height, 0, height, r);
    ctx.arcTo(0, height, 0, 0, r);
    ctx.arcTo(0, 0, width, 0, r);
    ctx.closePath();
    ctx.clip();

    ctx.drawImage(image, 0, 0, width, height);

    const backdrop = resolveBannerBackdropPalette({
      backgroundStyle: bannerConfig.backgroundStyle,
      backgroundSpec: bannerConfig.backgroundSpec,
      primaryColor: bannerConfig.primaryColor,
      accentColor: bannerConfig.accentColor,
    });

    const paintOverlay = (spec: string, alpha: number) => {
      const colors = String(spec || '')
        .match(/#[0-9a-fA-F]{6,8}/g)
        ?.slice(0, 3) || [];
      if (colors.length < 2) return;
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      gradient.addColorStop(0, colors[0]);
      gradient.addColorStop(0.55, colors[1]);
      gradient.addColorStop(1, colors[colors.length - 1] || colors[1]);
      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();
    };

    paintOverlay(backdrop.background, bannerConfig.backgroundStyle === 'light-clinical' ? 0.22 : 0.46);
    paintOverlay(backdrop.glow, bannerConfig.backgroundStyle === 'light-clinical' ? 0.16 : 0.32);

    const layout = getBannerLayoutMetrics(
      {
        ...bannerConfig,
        bannerImageUrl: backgroundDataUrl,
      },
      width,
      height
    );
    const palette = resolveBannerTonePalette(bannerConfig.accentTone);
    const textColor = resolveBannerTextColor(bannerConfig.textColor);

    const title = String(bannerConfig.title || 'ORTHO PRECISION').toUpperCase();
    const subtitle = String(bannerConfig.subtitle || 'MEDICAL CREDENTIAL').toUpperCase();
    const tagline = String(bannerConfig.tagline || '').trim();
    const supportText = String(bannerConfig.supportText || '').trim();
    const displayName = String(bannerConfig.displayName || bannerConfig.doctorName || 'OrthoPrecision');
    const rightLines = [
      String(bannerConfig.specialty || ''),
      bannerConfig.crm
        ? `CRM: ${bannerConfig.crm}${bannerConfig.crmState ? ` / ${bannerConfig.crmState}` : ''}`
        : bannerConfig.crmState
          ? `CRM / ${bannerConfig.crmState}`
          : '',
      bannerConfig.hospital ? `Hospital: ${bannerConfig.hospital}` : '',
      bannerConfig.phone ? `Telefone: ${bannerConfig.phone}` : '',
      bannerConfig.email ? `E-mail: ${bannerConfig.email}` : '',
      bannerConfig.site ? `Site: ${bannerConfig.site}` : '',
    ].filter(Boolean);

    const fitText = (text: string, maxWidth: number, font: string) => {
      ctx.font = font;
      if (ctx.measureText(text).width <= maxWidth) return text;
      const ellipsis = '...';
      let output = text;
      while (output.length > 0 && ctx.measureText(`${output}${ellipsis}`).width > maxWidth) {
        output = output.slice(0, -1);
      }
      return output ? `${output}${ellipsis}` : ellipsis;
    };

    const wrapText = (text: string, maxWidth: number, font: string, maxLines: number) => {
      ctx.font = font;
      const words = text.split(/\s+/).filter(Boolean);
      const lines: string[] = [];
      let current = '';
      for (const word of words) {
        const candidate = current ? `${current} ${word}` : word;
        if (ctx.measureText(candidate).width <= maxWidth) {
          current = candidate;
        } else {
          if (current) lines.push(current);
          current = word;
          if (lines.length >= maxLines) break;
        }
      }
      if (current && lines.length < maxLines) lines.push(current);
      if (lines.length > maxLines) return lines.slice(0, maxLines);
      if (lines.length === maxLines) {
        lines[maxLines - 1] = fitText(lines[maxLines - 1], maxWidth, font);
      }
      return lines;
    };

    ctx.textBaseline = 'alphabetic';
    ctx.fillStyle = textColor;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.35)';
    ctx.shadowBlur = 4;
    ctx.shadowOffsetY = 1;

    const leftTitleLines = wrapText(title, layout.leftWidth, `700 ${layout.titleFontSize}px Arial`, 3);
    let textY = layout.leftY + Math.max(18, Math.round(layout.titleFontSize * 0.9));
    leftTitleLines.forEach((line) => {
      ctx.font = `700 ${layout.titleFontSize}px Arial`;
      ctx.fillText(line, layout.leftX, textY);
      textY += Math.max(18, layout.titleLineHeight);
    });

    ctx.shadowBlur = 0;
    ctx.strokeStyle = 'rgba(73, 196, 255, 0.85)';
    ctx.lineWidth = Math.max(2, Math.round(height * 0.006));
    ctx.beginPath();
    ctx.moveTo(layout.leftX, textY + Math.round(height * 0.004));
    ctx.lineTo(layout.leftX + Math.round(width * 0.06), textY + Math.round(height * 0.004));
    ctx.stroke();

    ctx.fillStyle = palette.subtitle;
    ctx.font = `700 ${layout.subtitleFontSize}px Arial`;
    ctx.fillText(fitText(subtitle, layout.leftWidth, ctx.font), layout.leftX, textY + layout.subtitleGap);

    let leftContinuationY = textY + layout.subtitleGap + layout.taglineGap;

    if (tagline) {
      ctx.fillStyle = palette.tagline;
      ctx.font = `500 ${layout.taglineFontSize}px Arial`;
      const taglineLines = wrapText(tagline, layout.leftWidth, ctx.font, 2);
      const taglineY = textY + layout.subtitleGap + layout.taglineGap;
      const taglineLineHeight = Math.max(14, Math.round(layout.taglineFontSize * 1.16));
      taglineLines.forEach((line, index) => {
        ctx.fillText(
          fitText(line, layout.leftWidth, ctx.font),
          layout.leftX,
          taglineY + index * taglineLineHeight
        );
      });
      leftContinuationY = taglineY + taglineLines.length * taglineLineHeight;
    }

    if (supportText) {
      ctx.fillStyle = textColor;
      ctx.font = `600 ${layout.supportTextFontSize}px Arial`;
      const supportLines = wrapText(supportText.toUpperCase(), layout.leftWidth, ctx.font, 2);
      const supportY = leftContinuationY + layout.supportTextGap;
      const supportLineHeight = Math.max(13, Math.round(layout.supportTextFontSize * 1.14));
      supportLines.forEach((line, index) => {
        ctx.fillText(
          fitText(line, layout.leftWidth, ctx.font),
          layout.leftX,
          supportY + index * supportLineHeight
        );
      });
    }

    ctx.fillStyle = textColor;
    ctx.font = `700 ${layout.displayNameFontSize}px Arial`;
    ctx.fillText(fitText(displayName.toUpperCase(), layout.rightWidth, ctx.font), layout.rightX, layout.rightY + Math.round(layout.displayNameFontSize * 0.18));

    ctx.fillStyle = textColor;
    ctx.font = `400 ${layout.lineFontSize}px Arial`;
    rightLines.forEach((line, index) => {
      ctx.fillText(
        fitText(line, layout.rightWidth, ctx.font),
        layout.rightX,
        layout.rightY + layout.displayNameFontSize + Math.round(layout.subtitleGap * 0.75) + index * layout.lineGap
      );
    });

    return canvas.toDataURL('image/png');
  } catch (error) {
    console.error('Failed to render ortho header to png:', error);
    return backgroundDataUrl;
  }
};

const ORTHO_PDF_FONT_FAMILY = 'OrthoPrecisionSans';
const PDF_FALLBACK_FONT_FAMILY = 'helvetica';

const loadPdfFonts = async (pdf: jsPDF) => {
  const anyPdf = pdf as any;
  if (anyPdf.__orthoprecisionFontsLoaded) return;

  const fetchFontBase64 = async (url: string) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to load font: ${url}`);
    }
    const buffer = await response.arrayBuffer();
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const [regularBase64, boldBase64] = await Promise.all([
    fetchFontBase64('/fonts/arial.ttf'),
    fetchFontBase64('/fonts/arialbd.ttf'),
  ]);

  pdf.addFileToVFS('OrthoPrecisionSans.ttf', regularBase64);
  pdf.addFont('OrthoPrecisionSans.ttf', ORTHO_PDF_FONT_FAMILY, 'normal');
  pdf.addFileToVFS('OrthoPrecisionSans-Bold.ttf', boldBase64);
  pdf.addFont('OrthoPrecisionSans-Bold.ttf', ORTHO_PDF_FONT_FAMILY, 'bold');
  anyPdf.__orthoprecisionFontsLoaded = true;
};

const ConsultModule: React.FC<ConsultModuleProps> = ({ moduleView = 'pathologies' }) => {
  const { language, t } = useLanguage();
  const { patients, activePatient, setActivePatient, updatePatient } = usePatient();
  const db = getOrthoDatabase(language);
  const isReportAnalysisModule = moduleView === 'report_analysis';
  const showSidebar = !isReportAnalysisModule;
  const showModeTabs = moduleView === 'combined';

  // View Mode: 'search' or 'report'
  const [mode, setMode] = useState<'search' | 'report'>('search');

  const [searchQuery, setSearchQuery] = useState('');
  const [localFilter, setLocalFilter] = useState(''); 
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [aiResult, setAiResult] = useState<PathologyAiResult | null>(null);
  const [selectedDbItem, setSelectedDbItem] = useState<LocalPathology | null>(null);
  const [imageError, setImageError] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // OCR/Report State
  const [reportImage, setReportImage] = useState<string | null>(null);
  const [reportResult, setReportResult] = useState<MedicalReportAnalysisResult | null>(null);
  const [reportLoadingIndex, setReportLoadingIndex] = useState(0);
  const [copyPrescription, setCopyPrescription] = useState(false);
  const [selectedReportDocumentType, setSelectedReportDocumentType] = useState<MedicalReportDocumentType | null>(null);
  const [documentClinicalOpinion, setDocumentClinicalOpinion] = useState('');
  const [generatedDocument, setGeneratedDocument] = useState<GeneratedMedicalReportDocument | null>(null);
  const [isGeneratingDocument, setIsGeneratingDocument] = useState(false);
  const [documentError, setDocumentError] = useState('');
  const [copyDocument, setCopyDocument] = useState(false);
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [pdfExportError, setPdfExportError] = useState('');
  const [selectedReportPatientId, setSelectedReportPatientId] = useState('');
  const [showPatientFilePicker, setShowPatientFilePicker] = useState(false);
  const [isSavingReportToPatient, setIsSavingReportToPatient] = useState(false);
  const [reportPatientError, setReportPatientError] = useState('');
  const [reportPatientSaved, setReportPatientSaved] = useState(false);
  const [resultActionToast, setResultActionToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);
  const savedComplementaryPdfKeysRef = useRef<Set<string>>(new Set());
  const resultToastTimerRef = useRef<number | null>(null);
  const reportLoadingMessages = [
    'IA analisando o laudo...',
    'Extraindo achados...',
    'Sugerindo conduta...',
  ];

  useEffect(() => {
    if (!isAnalyzing) {
      setReportLoadingIndex(0);
      return;
    }

    const interval = window.setInterval(() => {
      setReportLoadingIndex((current) => (current + 1) % 3);
    }, 1800);

    return () => window.clearInterval(interval);
  }, [isAnalyzing]);

  useEffect(() => {
    return () => {
      if (resultToastTimerRef.current) {
        window.clearTimeout(resultToastTimerRef.current);
      }
      document.body.classList.remove('consult-pathology-printing');
    };
  }, []);
  
  // Custom Images State
  const [customImages, setCustomImages] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const reportInputRef = useRef<HTMLInputElement>(null);

  const getUsageMetricLabel = (metric: UsageMetric) => {
    if (metric === 'ocrMonthly') return t('usageOcrMonthlyLabel');
    if (metric === 'threeDMonthly') return t('usageThreeDMonthlyLabel');
    if (metric === 'patients') return t('usagePatientsLabel');
    return t('usageAnalysesMonthlyLabel');
  };

  const getUsageErrorMessage = (error: unknown) => {
    if (error instanceof UsageLimitError) {
      return t('usageLimitReachedMessage', {
        feature: getUsageMetricLabel(error.metric).toLowerCase(),
        plan: error.tier.toUpperCase(),
        limit:
          error.limit == null ? t('usageUnlimited') : String(error.limit),
      });
    }

    return error instanceof Error ? error.message : t('usageActionBlocked');
  };

  const pathologyLocale = language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR';

  const normalizeSharedText = (value: string) =>
    value
      .replace(/\r/g, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/^[\s>*-]+/gm, '- ')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]+\n/g, '\n')
      .trim();

  const formatPathologyDate = (date: Date) =>
    new Intl.DateTimeFormat(pathologyLocale, {
      dateStyle: 'long',
      timeStyle: 'short',
    }).format(date);

  const showResultToast = (message: string, tone: 'success' | 'error' = 'success') => {
    setResultActionToast({ message, tone });
    if (resultToastTimerRef.current) {
      window.clearTimeout(resultToastTimerRef.current);
    }
    resultToastTimerRef.current = window.setTimeout(() => {
      setResultActionToast(null);
      resultToastTimerRef.current = null;
    }, 2400);
  };

  const copyTextToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', 'true');
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const copied = document.execCommand('copy');
      document.body.removeChild(textarea);
      return copied;
    } catch (error) {
      console.error('Failed to copy pathology text:', error);
      return false;
    }
  };

  const getCurrentPathologyPdfSource = (): PathologyPdfSource | null => {
    if (aiResult) {
      return {
        title: normalizeSharedText(aiResult.nome),
        typeLabel: t('consultGenerativeAnalysis'),
        classification: normalizeMedicalMarkdownInput(aiResult.classificacao)
          .split('\n')
          .map((item) => normalizeSharedText(item))
          .filter(Boolean),
        examPhysical: normalizeMedicalMarkdownInput(aiResult.exameFisico)
          .split('\n')
          .map((item) => normalizeSharedText(item))
          .filter(Boolean),
        conduct: normalizeSharedText(normalizeMedicalMarkdownInput(aiResult.conduta)),
        materials: normalizeMedicalMarkdownInput(aiResult.opme)
          .split('\n')
          .map((item) => normalizeSharedText(item))
          .filter(Boolean),
        evidenceSections: pathologyAiCards
          .filter((card) => Boolean(cleanMedicalMarkdownText(card.value)))
          .map((card) => ({
            title: card.title,
            value: normalizeSharedText(normalizeMedicalMarkdownInput(card.value)),
          })),
      };
    }

    if (selectedDbItem) {
      const classification = selectedDbItem.classificacaoDetalhada.flatMap((cls) => [
        normalizeSharedText(cls.nome),
        ...cls.tipos.flatMap((tipo) => [
          normalizeSharedText(tipo.nome),
          normalizeSharedText(tipo.descricao),
        ]),
      ]).filter(Boolean);
      const conductParts = [
        selectedDbItem.condutaConservadora ? `${t('consultConservative')}: ${normalizeSharedText(selectedDbItem.condutaConservadora)}` : '',
        selectedDbItem.condutaCirurgica ? `${t('consultSurgical')}: ${normalizeSharedText(selectedDbItem.condutaCirurgica)}` : '',
      ].filter(Boolean);

      return {
        title: normalizeSharedText(selectedDbItem.nome),
        typeLabel: t('consultInstitutionalProtocol'),
        classification,
        examPhysical: [t('consultPathologyPrintNotApplicable')],
        conduct: conductParts.join('\n\n'),
        materials: (selectedDbItem.checklistMaterial || []).map((item) => normalizeSharedText(item)).filter(Boolean),
        evidenceSections: selectedDbItem.resumo
          ? [{
              title: t('consultPathologyPrintEvidence'),
              value: normalizeSharedText(selectedDbItem.resumo),
            }]
          : [],
      };
    }

    return null;
  };

  const buildPathologyPdfBlob = async (source: PathologyPdfSource) => {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    let fontFamily = PDF_FALLBACK_FONT_FAMILY;

    try {
      await loadPdfFonts(pdf);
      fontFamily = ORTHO_PDF_FONT_FAMILY;
    } catch (error) {
      console.warn('Falling back to default PDF font for pathology share:', error);
    }

    const setFont = (style: 'normal' | 'bold' = 'normal') => {
      pdf.setFont(fontFamily, style);
    };

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const left = 14;
    const right = 14;
    const top = 14;
    const bottom = 16;
    const contentWidth = pageWidth - left - right;
    const footerHeight = 10;
    const bodyBottom = pageHeight - bottom - footerHeight;
    const lineHeight = (size: number, factor = 1.32) => size * 0.352777778 * factor;
    const dateText = formatPathologyDate(new Date());

    const drawFooter = () => {
      setFont('normal');
      pdf.setFontSize(8);
      pdf.setTextColor(71, 85, 105);
      pdf.text(t('consultClinicalSupportDocumentDisclaimer'), left, pageHeight - 6.2, { maxWidth: contentWidth - 30 });
      pdf.text('OrthoPrecision', pageWidth - right, pageHeight - 6.2, { align: 'right' });
    };

    const drawHeader = () => {
      pdf.setFillColor(255, 255, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');
      pdf.setDrawColor(203, 213, 225);
      pdf.setLineWidth(0.3);
      pdf.line(left, 28, pageWidth - right, 28);

      setFont('bold');
      pdf.setFontSize(11);
      pdf.setTextColor(2, 132, 199);
      pdf.text('OrthoPrecision', left, 18);

      const titleLines = pdf.splitTextToSize(source.title, contentWidth - 58) as string[];
      pdf.setFontSize(17);
      pdf.setTextColor(15, 23, 42);
      titleLines.slice(0, 2).forEach((line, index) => {
        pdf.text(line, left, 33 + index * 6.2);
      });

      setFont('normal');
      pdf.setFontSize(9);
      pdf.setTextColor(71, 85, 105);
      pdf.text(source.typeLabel, left, 43 + Math.min(titleLines.length - 1, 1) * 6.2);
      pdf.text(`${t('consultPathologyPrintDate')}: ${dateText}`, pageWidth - right, 18, { align: 'right' });
      pdf.text(`${t('consultPathologyPrintIdentification')}: OrthoPrecision WEB`, pageWidth - right, 23, { align: 'right' });
    };

    let cursorY = 50;
    drawHeader();

    const ensureSpace = (needed: number) => {
      if (cursorY + needed <= bodyBottom) return;
      drawFooter();
      pdf.addPage();
      drawHeader();
      cursorY = 50;
    };

    const drawSection = (title: string, lines: string[]) => {
      if (!lines.length) return;
      const contentLines = lines.flatMap((line) => pdf.splitTextToSize(normalizeSharedText(line), contentWidth));
      const needed = 10 + contentLines.length * lineHeight(9.6) + 6;
      ensureSpace(needed);

      pdf.setDrawColor(203, 213, 225);
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(left, cursorY, contentWidth, needed - 2, 4, 4, 'FD');
      pdf.setFontSize(9.2);
      setFont('bold');
      pdf.setTextColor(15, 23, 42);
      pdf.text(title.toUpperCase(), left + 4, cursorY + 7);

      setFont('normal');
      pdf.setFontSize(9.5);
      pdf.setTextColor(15, 23, 42);
      let lineY = cursorY + 13;
      lines.forEach((line) => {
        const wrapped = pdf.splitTextToSize(normalizeSharedText(line), contentWidth - 8) as string[];
        wrapped.forEach((wrappedLine, index) => {
          pdf.text(wrappedLine, left + 4, lineY + index * lineHeight(9.5));
        });
        lineY += wrapped.length * lineHeight(9.5) + 3.2;
      });
      cursorY += needed;
    };

    const drawListSection = (title: string, items: string[]) => {
      const cleaned = items.map((item) => normalizeSharedText(item)).filter(Boolean);
      if (!cleaned.length) return;
      const wrappedItems = cleaned.flatMap((item) => pdf.splitTextToSize(item, contentWidth - 10) as string[]);
      const needed = 10 + wrappedItems.length * lineHeight(9.3) + cleaned.length * 1.8 + 6;
      ensureSpace(needed);

      pdf.setDrawColor(203, 213, 225);
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(left, cursorY, contentWidth, needed - 2, 4, 4, 'FD');
      setFont('bold');
      pdf.setFontSize(9.2);
      pdf.setTextColor(15, 23, 42);
      pdf.text(title.toUpperCase(), left + 4, cursorY + 7);

      setFont('normal');
      pdf.setFontSize(9.3);
      pdf.setTextColor(15, 23, 42);
      let lineY = cursorY + 13;
      cleaned.forEach((item) => {
        const wrapped = pdf.splitTextToSize(item, contentWidth - 12) as string[];
        pdf.text('•', left + 4, lineY);
        wrapped.forEach((wrappedLine, index) => {
          pdf.text(wrappedLine, left + 8, lineY + index * lineHeight(9.3));
        });
        lineY += wrapped.length * lineHeight(9.3) + 2.8;
      });
      cursorY += needed;
    };

    drawSection(t('consultPathologyPrintResultTitle'), [source.title, source.typeLabel]);
    drawListSection(t('consultClassification'), source.classification.length ? source.classification : [t('consultPathologyPrintNotApplicable')]);
    drawListSection(t('consultPhysicalExam'), source.examPhysical.length ? source.examPhysical : [t('consultPathologyPrintNotApplicable')]);
    drawSection(t('consultSuggestedConduct'), source.conduct ? [source.conduct] : [t('consultPathologyPrintNotApplicable')]);
    drawListSection(t('consultOpmeSuggestion'), source.materials.length ? source.materials : []);
    source.evidenceSections.forEach((section) => drawSection(section.title, [section.value]));

    drawFooter();
    return pdf.output('blob');
  };

  const downloadPathologyPdfBlob = (blob: Blob, fileName: string) => {
    const downloadUrl = URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.href = downloadUrl;
    downloadLink.download = fileName;
    downloadLink.click();
    window.setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
  };

  const handleSharePathologyPdf = async () => {
    const source = getCurrentPathologyPdfSource();
    if (!source) return;

    try {
      const pdfBlob = await buildPathologyPdfBlob(source);
      const slug = normalizeSharedText(source.title)
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60) || 'patologia';
      const fileName = `orthoprecision-patologia-${slug}.pdf`;
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
      const canShareFiles = typeof navigator.canShare === 'function' ? navigator.canShare({ files: [pdfFile] }) : false;

      if (navigator.share && canShareFiles) {
        await navigator.share({
          title: t('consultPathologyPrintHeader'),
          files: [pdfFile],
        });
        return;
      }

      downloadPathologyPdfBlob(pdfBlob, fileName);
      showResultToast(t('consultPdfShareFallbackDownload'));
    } catch (error) {
      console.error(error);
      try {
        const pdfBlob = await buildPathologyPdfBlob(source);
        const slug = normalizeSharedText(source.title)
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 60) || 'patologia';
        const fileName = `orthoprecision-patologia-${slug}.pdf`;
        downloadPathologyPdfBlob(pdfBlob, fileName);
        showResultToast(t('consultPdfShareFallbackDownload'));
      } catch (fallbackError) {
        console.error(fallbackError);
        showResultToast(t('consultShareError'), 'error');
      }
    }
  };

  const renderPathologyActions = (
    showSaveButton: boolean,
    saveLabelKey: 'consultSave' | 'consultSaveToRecord' = 'consultSave'
  ) => (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {showSaveButton && activePatient && (
        <button
          onClick={handleSaveToPatient}
          disabled={saved}
          className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-bold uppercase tracking-wide transition-all sm:px-4 ${
            saved
              ? 'border-emerald-500/50 bg-emerald-500/20 text-emerald-300'
              : 'border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] text-[color:var(--op-text)] hover:border-[color:var(--op-border-strong)] hover:bg-white/[0.04]'
          }`}
          title={saved ? t('consultSaved') : t(saveLabelKey)}
        >
          {saved ? <Check className="w-4 h-4" /> : <FilePlus className="w-4 h-4" />}
          <span className="hidden sm:inline">{saved ? t('consultSaved') : t(saveLabelKey)}</span>
        </button>
      )}
      <button
        type="button"
        onClick={handleSharePathologyPdf}
        className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-3 py-2 text-xs font-bold uppercase tracking-wide text-[color:var(--op-text)] transition-all hover:border-[color:var(--op-border-strong)] hover:bg-white/[0.04] sm:px-4"
        title={t('consultShare')}
      >
        <Share2 className="h-4 w-4 text-cyan-300" />
        <span className="hidden sm:inline">{t('consultShare')}</span>
      </button>
      <button
        type="button"
        onClick={handlePrintPathologyResult}
        className="inline-flex items-center gap-2 rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-3 py-2 text-xs font-bold uppercase tracking-wide text-[color:var(--op-text)] transition-all hover:border-[color:var(--op-border-strong)] hover:bg-white/[0.04] sm:px-4"
        title={t('consultPrint')}
      >
        <Printer className="h-4 w-4 text-cyan-300" />
        <span className="hidden sm:inline">{t('consultPrint')}</span>
      </button>
    </div>
  );

  const handlePrintPathologyResult = () => {
    if (!aiResult) return;

    const removePrintClass = () => {
      document.body.classList.remove('consult-pathology-printing');
    };

    document.body.classList.add('consult-pathology-printing');
    window.addEventListener('afterprint', removePrintClass, { once: true });
    window.setTimeout(removePrintClass, 3500);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        window.print();
      });
    });
  };

  const pathologyAiCards = aiResult
    ? [
        {
          key: 'definicao',
          title: t('consultPathologyDefinition'),
          value: aiResult.definicao,
          icon: <FileText className="h-4 w-4" />,
          tone: 'sky' as const,
        },
        {
          key: 'etiologia',
          title: t('consultPathologyEtiology'),
          value: aiResult.etiologia,
          icon: <AlertTriangle className="h-4 w-4" />,
          tone: 'amber' as const,
        },
        {
          key: 'classificacao',
          title: t('consultClassification'),
          value: aiResult.classificacao,
          icon: <Layers className="h-4 w-4" />,
          tone: 'violet' as const,
        },
        {
          key: 'exameFisico',
          title: t('consultPhysicalExam'),
          value: aiResult.exameFisico,
          icon: <Activity className="h-4 w-4" />,
          tone: 'cyan' as const,
        },
        {
          key: 'tiposConduta',
          title: t('consultPathologyConductTypes'),
          value: aiResult.tiposConduta,
          icon: <ClipboardList className="h-4 w-4" />,
          tone: 'indigo' as const,
        },
        {
          key: 'tratamentoDetalhado',
          title: t('consultPathologyDetailedTreatment'),
          value: aiResult.tratamentoDetalhado,
          icon: <Stethoscope className="h-4 w-4" />,
          tone: 'emerald' as const,
        },
        {
          key: 'conduta',
          title: t('consultSuggestedConduct'),
          value: aiResult.conduta,
          icon: <Sparkles className="h-4 w-4" />,
          tone: 'rose' as const,
        },
        {
          key: 'opme',
          title: t('consultOpmeSuggestion'),
          value: aiResult.opme,
          icon: <Database className="h-4 w-4" />,
          tone: 'cyan' as const,
        },
        {
          key: 'prognostico',
          title: t('consultPathologyPrognosis'),
          value: aiResult.prognostico,
          icon: <Zap className="h-4 w-4" />,
          tone: 'yellow' as const,
        },
        {
          key: 'reabilitacao',
          title: t('consultPathologyRehabilitation'),
          value: aiResult.reabilitacao,
          icon: <Check className="h-4 w-4" />,
          tone: 'sky' as const,
        },
      ].filter((card) => Boolean(cleanMedicalMarkdownText(card.value)))
    : [];

  const reportDocumentOptions: ReportDocumentOption[] = useMemo(() => ([
    { type: 'technical_report', labelKey: 'consultDocumentTechnicalReport', icon: FileText, featured: true },
    { type: 'functional_opinion', labelKey: 'consultDocumentFunctionalOpinion', icon: Stethoscope },
    { type: 'clinical_report', labelKey: 'consultDocumentClinicalReport', icon: Activity },
    { type: 'patient_summary', labelKey: 'consultDocumentPatientSummary', icon: User },
  ]), []);

  const selectedReportDocumentLabel = useMemo(() => {
    if (!selectedReportDocumentType) return '';
    const option = reportDocumentOptions.find((item) => item.type === selectedReportDocumentType);
    return option ? t(option.labelKey) : '';
  }, [reportDocumentOptions, selectedReportDocumentType, t]);

  const selectedReportPatient = useMemo(() => {
    if (!selectedReportPatientId) return activePatient ?? null;
    return patients.find((patient) => patient.id === selectedReportPatientId) || null;
  }, [activePatient, patients, selectedReportPatientId]);

  const normalizedGeneratedDocumentContent = useMemo(
    () => normalizeComplementaryDocumentContent(generatedDocument?.content || ''),
    [generatedDocument?.content]
  );

  const resolveDoctorProfile = async (patientDoctorSource?: any): Promise<PdfDoctorProfile> => {
    const uid = auth.currentUser?.uid;
    let firestoreProfile: any = null;
    if (uid) {
      try {
        const snapshot = await getDoc(doc(firestoreDb, 'users', uid));
        if (snapshot.exists()) {
          firestoreProfile = snapshot.data();
        }
      } catch (error) {
        console.error('Falha ao carregar perfil do médico para PDF:', error);
      }
    }

    let storageProfile: any = null;
    try {
      const stored = localStorage.getItem('orthoprecision_users');
      const currentUserKey = localStorage.getItem('orthoprecision_current_user') || '';
      if (stored) {
        const parsed = JSON.parse(stored);
        const candidates = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.users)
            ? parsed.users
            : parsed?.currentUser
              ? [parsed.currentUser]
              : currentUserKey && parsed?.[currentUserKey]
                ? [parsed[currentUserKey]]
                : [parsed];

        const currentEmail = auth.currentUser?.email?.toLowerCase();
        const matchingCandidate = candidates.find((item: any) => {
          const itemEmail = String(item?.email || item?.profile?.email || '').toLowerCase();
          const itemOrthoId = String(item?.orthoId || item?.profile?.orthoId || '').toLowerCase();
          return (
            (uid && String(item?.uid || item?.id || item?.authUid || '') === uid) ||
            (currentEmail && itemEmail === currentEmail) ||
            (uid && itemOrthoId === uid.toLowerCase())
          );
        });

        if (matchingCandidate) {
          storageProfile = matchingCandidate;
        }
      }
    } catch (error) {
      console.error('Falha ao ler perfil salvo do médico:', error);
    }

    const normalized = normalizeDoctorProfile(
      firestoreProfile,
      storageProfile,
      activePatient?.responsibleDoctor,
      patientDoctorSource,
      activePatient?.doctor,
      auth.currentUser
    );

    const resolved = buildDoctorProfilePayload(normalized);
    const bannerStorageKey = `orthoprecision_doctor_banner_${uid || auth.currentUser?.email || "default"}`;
    const storedBannerOverride = (() => {
      try {
        return localStorage.getItem(bannerStorageKey)?.trim() || "";
      } catch {
        return "";
      }
    })();
    const bannerModeStorageKey = `orthoprecision_doctor_banner_mode_${uid || auth.currentUser?.email || "default"}`;
    const storedBannerMode = (() => {
      try {
        return (localStorage.getItem(bannerModeStorageKey)?.trim() || "ai").toLowerCase();
      } catch {
        return "ai";
      }
    })();

    return {
      name: resolved.fullName || auth.currentUser?.displayName || auth.currentUser?.email || '',
      headerTitlePrimary: resolved.headerTitlePrimary || undefined,
      headerTitleSecondary: resolved.headerTitleSecondary || undefined,
      bannerTagline: resolved.bannerTagline || undefined,
      bannerSupportText: resolved.bannerSupportText || undefined,
      bannerAccentTone: resolved.bannerAccentTone || undefined,
      bannerTextColor: resolved.bannerTextColor || undefined,
      bannerPrimaryColor: resolved.bannerPrimaryColor || undefined,
      bannerAccentColor: resolved.bannerAccentColor || undefined,
      bannerBackgroundStyle: resolved.bannerBackgroundStyle || undefined,
      bannerBackgroundSpec: resolved.bannerBackgroundSpec || undefined,
      bannerLayoutVariant: resolved.bannerLayoutVariant || undefined,
      bannerLayoutSpec: resolved.bannerLayoutSpec || undefined,
      bannerPaletteSpec: resolved.bannerPaletteSpec || undefined,
      bannerTypographySpec: resolved.bannerTypographySpec || undefined,
      bannerDecorativeSpec: resolved.bannerDecorativeSpec || undefined,
      headerName: resolved.headerName || resolved.hospital || resolved.fullName || auth.currentUser?.displayName || '',
      bannerMode: storedBannerMode === 'upload' ? 'upload' : (resolved.bannerMode || 'ai'),
      bannerTemplateId: resolved.bannerTemplateId || undefined,
      bannerLayoutPreset: resolved.bannerLayoutPreset || undefined,
      bannerOffsetLeftX: resolved.bannerOffsetLeftX,
      bannerOffsetLeftY: resolved.bannerOffsetLeftY,
      bannerOffsetRightX: resolved.bannerOffsetRightX,
      bannerOffsetRightY: resolved.bannerOffsetRightY,
      crm: resolved.crm,
      crmState: resolved.crmState,
      rqe: resolved.rqe,
      specialty: resolved.specialty,
      subspecialty: resolved.subspecialty,
      hospital: resolved.hospital,
      phone: resolved.phone || undefined,
      email: resolved.email || undefined,
      website: resolved.website || undefined,
      orthoId: resolved.orthoId || auth.currentUser?.email || auth.currentUser?.uid || '',
      logo: resolved.logo || undefined,
      banner: (storedBannerMode === 'upload' || resolved.bannerMode === 'upload') ? (storedBannerOverride || resolved.banner || undefined) : undefined,
      signature: resolved.signature || undefined,
    };
  };

  // Load custom images from local storage on mount
  useEffect(() => {
    const savedImages = localStorage.getItem('orthoprecision_consult_images');
    if (savedImages) {
      try {
        setCustomImages(JSON.parse(savedImages));
      } catch (e) {
        console.error("Failed to load custom images", e);
      }
    }
  }, []);

  useEffect(() => {
    setSaved(false);
  }, [aiResult, selectedDbItem, reportResult]);

  useEffect(() => {
    setSelectedReportDocumentType(null);
    setDocumentClinicalOpinion('');
    setGeneratedDocument(null);
    setDocumentError('');
    setCopyDocument(false);
    setPdfExportError('');
    setIsExportingPdf(false);
    setReportPatientError('');
    setReportPatientSaved(false);
  }, [reportResult]);

  useEffect(() => {
    if (!selectedReportPatientId && activePatient?.id && reportImage) {
      setSelectedReportPatientId(activePatient.id);
    }
  }, [activePatient?.id, reportImage, selectedReportPatientId]);

  useEffect(() => {
    if (moduleView === 'report_analysis' && mode !== 'report') {
      setMode('report');
      return;
    }

    if (moduleView === 'pathologies' && mode !== 'search') {
      setMode('search');
    }
  }, [moduleView, mode]);

  // Helper to determine icon based on taxonomy/category
  const getCategoryIcon = (taxonomy: LocalPathology['taxonomy']) => {
    const cats = taxonomy.pathology.map(p => p.toLowerCase());
    if (cats.includes('pediátrica') || cats.includes('pediatric')) return <Baby className="w-4 h-4 text-pink-400" />;
    if (cats.includes('infeção') || cats.includes('infection')) return <Skull className="w-4 h-4 text-emerald-400" />;
    if (cats.includes('tumor')) return <Zap className="w-4 h-4 text-purple-400" />;
    if (cats.includes('trauma')) return <Bone className="w-4 h-4 text-blue-400" />;
    return <Activity className="w-4 h-4 text-slate-400" />;
  };

  // Filtered list for the sidebar
  const filteredDb = useMemo(() => {
    if (!localFilter) return db;
    const q = localFilter.toLowerCase();
    return db.filter(item => {
      const nameMatch = item.nome.toLowerCase().includes(q);
      const synMatch = item.synonyms?.some(s => s.toLowerCase().includes(q));
      const anatMatch = item.taxonomy.anatomy.some(a => a.toLowerCase().includes(q));
      return nameMatch || synMatch || anatMatch;
    });
  }, [db, localFilter]);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    const q = searchQuery.toLowerCase();

    // 1. Smart Local Search (Check Name, Synonyms, Taxonomy)
    const localMatches = db.filter(item => {
      const nameMatch = item.nome.toLowerCase().includes(q);
      const synMatch = item.synonyms?.some(s => s.toLowerCase().includes(q));
      const anatMatch = item.taxonomy.anatomy.some(a => a.toLowerCase().includes(q));
      const pathMatch = item.taxonomy.pathology.some(p => p.toLowerCase().includes(q));
      return nameMatch || synMatch || anatMatch || pathMatch;
    });

    if (localMatches.length > 0) {
      const exactMatch = localMatches.find(m => m.nome.toLowerCase() === q);
      setSelectedDbItem(exactMatch || localMatches[0]);
      setAiResult(null);
      setImageError(false);
      return;
    }

    // 2. If no local match, use AI
    setSelectedDbItem(null);
    setIsAnalyzing(true);
    setAiResult(null);
    const uid = auth.currentUser?.uid;

    try {
      if (!uid) {
        throw new Error(t('usageAuthenticationRequired'));
      }

      const result = await analyzeDisease(searchQuery, language);
      setAiResult(result);
    } catch (error) {
      console.error(error);
      alert(getUsageErrorMessage(error));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSelectDbItem = (item: LocalPathology) => {
    setSelectedDbItem(item);
    setAiResult(null);
    setSearchQuery('');
    setImageError(false);
    setMode('search');
  };

  const handleSaveToPatient = () => {
    if (!activePatient) {
        alert(t("selectPatientFirst"));
        return;
    }

    let title = "";
    let content = "";

    if (mode === 'search') {
        if (selectedDbItem) {
            title = t('consultSavedProtocolTitle', { name: selectedDbItem.nome });
            content = `${t('consultSavedSummary', { summary: selectedDbItem.resumo })}\n\n${t('consultSavedConduct', {
                conduct: selectedDbItem.condutaCirurgica || selectedDbItem.condutaConservadora || '',
            })}`;
        } else if (aiResult) {
            title = t('consultAiAnalysisTitle', { name: aiResult.nome });
            content = `${t('consultSavedDiagnosis', { diagnosis: aiResult.nome })}\n\n${t('consultSavedSuggestedConduct', {
                conduct: pathologyText(aiResult.conduta),
            })}\n\n${t('consultSavedSuggestedOpme', { opme: pathologyLines(aiResult.opme).join(', ') || '-' })}`;
        }
    } else if (mode === 'report' && reportResult) {
        const findings = reportResult.findings?.join(', ') || '-';
        const meds = reportResult.prescription?.medications?.map((m: any) => `${m.name} - ${m.instructions}`).join('\n') || '-';
        const careInstructions = reportResult.prescription?.careInstructions?.join('\n') || '-';

        title = reportResult.diagnosis
          ? `${t('consultAiReportRecordTitle')}: ${reportResult.diagnosis}`
          : t('consultAiReportRecordTitle');

        content = `${t('consultRecordSourceLabel')}: ${t('consultRecordSourceOcr')}\n${t('consultRecordPatientIdLabel')}: ${activePatient.id}\n\n${t('consultRecordDiagnosisLabel')}: ${reportResult.diagnosis || '-'}\n\n${t('consultSavedFindings', {
            findings,
        })}\n\n${t('consultRecordReasoningLabel')}: ${reportResult.reasoning || '-'}\n\n${t('consultRecordTherapeuticPlanLabel')}: ${reportResult.treatmentPlan || '-'}\n\n${t('consultSavedPrescription', { meds })}\n\n${t('consultGeneralGuidance')}:\n${careInstructions}`;
    }

    if (!title) return;

    const newNote: ClinicalNote = {
        id: Date.now().toString(),
        date: Date.now(),
        type: 'Consult',
        title: title,
        content: content
    };

    updatePatient(activePatient.id, {
        clinicalNotes: [newNote, ...(activePatient.clinicalNotes || [])]
    });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  const handleCopyPrescription = () => {
    if (!reportResult?.prescription) return;
    
    const meds = reportResult.prescription.medications?.map((m: any) => `- ${m.name}\n  ${m.instructions}`).join('\n\n');
    const care = reportResult.prescription.careInstructions?.map((c: string) => `- ${c}`).join('\n');
    
    const text = `${t('consultPrescriptionSuggestedHeader')}\n\n${t('consultOralUse').toUpperCase()}:\n\n${meds}\n\n${t('consultGeneralGuidance').toUpperCase()}:\n${care}`;
    
    navigator.clipboard.writeText(text);
    setCopyPrescription(true);
    setTimeout(() => setCopyPrescription(false), 2000);
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedDbItem) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target?.result as string;
        img.onload = () => {
          // Resize to save LocalStorage space
          const canvas = document.createElement('canvas');
          const MAX_WIDTH = 800;
          const scaleSize = MAX_WIDTH / img.width;
          canvas.width = MAX_WIDTH;
          canvas.height = img.height * scaleSize;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
          
          const base64Url = canvas.toDataURL('image/jpeg', 0.7);
          
          const newImages = { ...customImages, [selectedDbItem.id]: base64Url };
          setCustomImages(newImages);
          localStorage.setItem('orthoprecision_consult_images', JSON.stringify(newImages));
          setImageError(false);
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveCustomImage = () => {
    if (selectedDbItem) {
      const newImages = { ...customImages };
      delete newImages[selectedDbItem.id];
      setCustomImages(newImages);
      localStorage.setItem('orthoprecision_consult_images', JSON.stringify(newImages));
      setImageError(false); // Reset to try loading default image
    }
  };

  // OCR Logic
  const handleReportUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setReportImage(reader.result as string);
        setReportResult(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const checkAllergyConflict = (medName: string) => {
    if (!activePatient?.allergies) return false;
    return (activePatient.allergies ?? []).some(allergy => 
      medName.toLowerCase().includes(allergy.toLowerCase()) || 
      allergy.toLowerCase().includes(medName.toLowerCase())
    );
  };

  const analyzeReport = async () => {
    if (!reportImage) return;
    setIsAnalyzing(true);
    setSelectedReportDocumentType(null);
    setGeneratedDocument(null);
    setDocumentError('');
    setCopyDocument(false);
    const uid = auth.currentUser?.uid;
    try {
        if (!uid) {
          throw new Error(t('usageAuthenticationRequired'));
        }

        const result = await analyzeMedicalReport(reportImage, language, selectedReportPatient ? {
          age: selectedReportPatient.age,
          anatomy: selectedReportPatient.anatomy,
          chiefComplaint: selectedReportPatient.chiefComplaint,
          observations: selectedReportPatient.historyOfPresentIllness || selectedReportPatient.notes,
          comorbidities: selectedReportPatient.comorbidities,
          allergies: selectedReportPatient.allergies,
          priorTreatments: selectedReportPatient.initialPlan,
        } : undefined);
        setReportResult(result);
    } catch (e) {
        console.error(e);
        alert(
          e instanceof UsageLimitError || (e as any)?.debugCode === "ATLAS_RATE_LIMIT"
            ? getUsageErrorMessage(e)
            : e instanceof Error && e.message !== t('usageAuthenticationRequired')
            ? t("consultAnalyzeReportError")
            : getUsageErrorMessage(e)
        );
    } finally {
        setIsAnalyzing(false);
    }
  };

  const buildSelectedPatientContext = (patient: Patient) => ({
    id: patient.id,
    name: patient.name,
    age: patient.age,
    sex: patient.sex,
    anatomy: patient.anatomy,
    cpf: patient.cpf || '',
    phone: patient.phone || '',
    email: patient.email || '',
    doctorOrthoId: patient.doctorOrthoId || '',
    responsibleDoctorName: patient.responsibleDoctorName || patient.responsibleDoctor?.name || '',
    profession: (patient as any).profession || '',
    physicalExam: patient.physicalExam || '',
  });

  const handleSelectReportDocument = (documentType: MedicalReportDocumentType) => {
    setSelectedReportDocumentType(documentType);
    setGeneratedDocument(null);
    setDocumentError('');
    setCopyDocument(false);
  };

  const handleGenerateReportDocument = async () => {
    if (!reportResult || !selectedReportDocumentType) return;
    if (!selectedReportPatient) {
      setDocumentError(t('consultSelectPatientRequired'));
      return;
    }

    setIsGeneratingDocument(true);
    setDocumentError('');
    setCopyDocument(false);

    try {
      const generated = await generateMedicalReportDocument(
        reportResult,
        selectedReportDocumentType,
        documentClinicalOpinion.trim(),
        language,
        buildSelectedPatientContext(selectedReportPatient)
      );
      setGeneratedDocument({
        ...generated,
        content: normalizeComplementaryDocumentContent(generated.content),
        title: normalizePdfText(generated.title),
      });
    } catch (error) {
      console.error(error);
      setDocumentError(
        error instanceof Error ? error.message : t('consultDocumentGenerationError')
      );
    } finally {
      setIsGeneratingDocument(false);
    }
  };

  const handleAddReportToPatientRecord = async () => {
    if (!reportResult) return;
    if (!selectedReportPatient) {
      setReportPatientError(t('consultSelectPatientRequired'));
      return;
    }

    const uid = auth.currentUser?.uid;
    if (!uid) {
      setReportPatientError(t('usageAuthenticationRequired'));
      return;
    }

    setIsSavingReportToPatient(true);
    setReportPatientError('');
    setReportPatientSaved(false);

    try {
      const uploadedAttachment = reportImage && reportImage.startsWith('data:')
        ? await uploadPatientDataUrl({
            uid,
            patientId: selectedReportPatient.id,
            dataUrl: reportImage,
          })
        : null;

      const fileName = uploadedAttachment
        ? `${getSafeFileName(`${selectedReportPatient.name || 'patient'}_${reportResult.diagnosis || 'report'}_${Date.now()}`)}.${uploadedAttachment.contentType.includes('pdf') ? 'pdf' : uploadedAttachment.contentType.includes('png') ? 'png' : uploadedAttachment.contentType.includes('jpeg') || uploadedAttachment.contentType.includes('jpg') ? 'jpg' : 'bin'}`
        : '';

      const patientFile: PatientFile | null = uploadedAttachment
        ? {
            ...uploadedAttachment,
            name: fileName || uploadedAttachment.name,
          }
        : null;

      const generatedText = generatedDocument
        ? `${generatedDocument.title}\n\n${normalizedGeneratedDocumentContent}`
        : '';

      const noteSections = [
        `${t('consultRecordSourceLabel')}: ${t('consultRecordSourceOcr')}`,
        `${t('consultRecordPatientIdLabel')}: ${selectedReportPatient.id}`,
        `${t('consultRecordPatientNameLabel')}: ${selectedReportPatient.name}`,
        `${t('consultRecordDiagnosisLabel')}: ${reportResult.diagnosis || '-'}`,
        `${t('consultRelevantFindings')}: ${reportResult.findings?.join(', ') || '-'}`,
        `${t('consultClinicalReasoning')}: ${reportResult.reasoning || '-'}`,
        `${t('consultDetailedPlan')}: ${stripMarkdown(reportResult.treatmentPlan || '-')}`,
        reportResult.rehabProtocol
          ? `${t('consultRehabilitation')}: ${reportResult.rehabProtocol}`
          : '',
        reportResult.prescription?.medications?.length
          ? `${t('consultPrescriptionGuide')}:\n${reportResult.prescription.medications
              .map((med: any) => `- ${med.name} - ${med.instructions}`)
              .join('\n')}`
          : '',
        reportResult.prescription?.careInstructions?.length
          ? `${t('consultGeneralGuidance')}:\n${reportResult.prescription.careInstructions
              .map((item: string) => `- ${item}`)
              .join('\n')}`
          : '',
        documentClinicalOpinion.trim()
          ? `${t('consultDocumentClinicalOpinionLabel')}: ${documentClinicalOpinion.trim()}`
          : '',
        generatedText
          ? `${t('consultGeneratedDocumentTitle')}: ${generatedText}`
          : '',
        patientFile
          ? `${t('consultRecordUploadLabel')}: ${patientFile.name || patientFile.path}`
          : '',
        patientFile?.url
          ? `${t('consultRecordUploadUrlLabel')}: ${patientFile.url}`
          : '',
      ].filter(Boolean);

      const newNote: ClinicalNote = {
        id: `${Date.now()}`,
        date: Date.now(),
        type: 'Document',
        title: reportResult.diagnosis
          ? `${t('consultAiReportRecordTitle')}: ${reportResult.diagnosis}`
          : t('consultAiReportRecordTitle'),
        content: noteSections.join('\n\n'),
      };

      const currentPatient = selectedReportPatient;
      const currentFiles = Array.isArray((currentPatient as any).patientFiles)
        ? [...((currentPatient as any).patientFiles as PatientFile[])]
        : [];

      const updatedPatient = {
        ...currentPatient,
        clinicalNotes: [newNote, ...(currentPatient.clinicalNotes || [])],
        ...(patientFile
          ? {
              patientFiles: [patientFile, ...currentFiles],
            }
          : {}),
      };

      await updatePatient(currentPatient.id, {
        clinicalNotes: [newNote, ...(currentPatient.clinicalNotes || [])],
        ...(patientFile
          ? {
              patientFiles: [patientFile, ...currentFiles],
            }
          : {}),
      } as any);

      setActivePatient(updatedPatient as Patient);
      setReportPatientSaved(true);
      setTimeout(() => setReportPatientSaved(false), 2500);
    } catch (error) {
      console.error(error);
      setReportPatientError(
        error instanceof Error ? error.message : t('saveRecordError')
      );
    } finally {
      setIsSavingReportToPatient(false);
    }
  };

  const handleCopyGeneratedDocument = async () => {
    if (!generatedDocument) return;

    try {
      await navigator.clipboard.writeText(`${generatedDocument.title}\n\n${normalizedGeneratedDocumentContent}`);
      setCopyDocument(true);
      setTimeout(() => setCopyDocument(false), 2000);
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportProfessionalPdf = async () => {
    if (!generatedDocument) return;

    setIsExportingPdf(true);
    setPdfExportError('');

    try {
      const patient = selectedReportPatient || null;
      const profile = await resolveDoctorProfile(patient?.responsibleDoctor || (patient as any)?.doctor);

      const emittedAt = new Date();
      const formattedDate = emittedAt.toLocaleDateString(language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      });
      const formattedTime = emittedAt.toLocaleTimeString(language === 'en' ? 'en-US' : language === 'es' ? 'es-ES' : 'pt-BR', {
        hour: '2-digit',
        minute: '2-digit',
      });
      const fileNameBase = getSafeFileName(
        `${selectedReportDocumentLabel || generatedDocument.title} ${profile.name || 'OrthoPrecision'} ${emittedAt.getFullYear()}${String(emittedAt.getMonth() + 1).padStart(2, '0')}${String(emittedAt.getDate()).padStart(2, '0')}`
      );

      const uid = auth.currentUser?.uid;
      if (!uid) {
        throw new Error(t('usageAuthenticationRequired'));
      }

      const verificationBundle = await buildReportVerificationBundle({
        userId: uid,
        patientId: patient?.id || null,
        reportType: selectedReportDocumentType || 'technical_report',
        title: selectedReportDocumentLabel || generatedDocument.title,
        doctorName: profile.name,
        doctorCrm: profile.crm,
        doctorCrmState: profile.crmState,
        doctorRqe: profile.rqe || '',
        doctorSpecialty: profile.specialty || '',
        doctorSubspecialty: profile.subspecialty || '',
        orthoId: profile.orthoId || '',
        patientName: patient?.name || '',
        anatomy: patient?.anatomy || null,
        normalizedContent: normalizedGeneratedDocumentContent,
        issuedAt: emittedAt,
        issuedAtFormatted: `${formattedDate} • ${formattedTime}`,
        pdfVersion: 'orthoprecision-report-v1',
        baseUrl: window.location.origin,
      });

      const [qrDataUrl] = await Promise.all([
        generateQrCodeDataUrl(verificationBundle.verificationUrl),
        saveGeneratedReportRecord(verificationBundle),
      ]);

      const resolvePdfLogoDataUrl = async (candidate?: string | null) => {
        const source = String(candidate || '').trim();
        if (source) {
          const customLogo = await fetchImageAsDataUrl(source);
          if (import.meta.env.DEV) {
            console.log('[Consult PDF] logo resolution', {
              stage: 'resolve',
              hasCandidate: Boolean(source),
              usedCustomLogo: Boolean(customLogo),
              sourcePreview: source.slice(0, 48),
            });
          }
          if (customLogo) return customLogo;
          if (/^data:/i.test(source)) return source;
          return source;
        }

        if (import.meta.env.DEV) {
          console.log('[Consult PDF] logo resolution', {
            stage: 'fallback',
            hasCandidate: Boolean(source),
            usedCustomLogo: false,
            sourcePreview: source.slice(0, 48),
          });
        }

        return await fetchImageAsDataUrl('/ortho-logo.png');
      };

      const resolvePdfBannerDataUrl = async (candidate?: string | null) => {
        const loadRenderableImage = async (source: string) => {
          const fetched = await fetchImageAsDataUrl(source);
          const resolved = fetched || (/^data:/i.test(source) ? source : source || null);
          if (!resolved) return null;
          if (isSvgDataUrl(resolved)) {
            const rasterized = await rasterizeImageDataUrlToPng(resolved);
            if (rasterized) return rasterized;
          }
          return resolved;
        };

        const source = String(candidate || '').trim();
        if (source) {
          const bannerDataUrl = await loadRenderableImage(source);
          if (bannerDataUrl) {
            return bannerDataUrl;
          }
        }

        const fallbackBannerDataUrl = await loadRenderableImage('/ortho-banner-bg.svg');
        return fallbackBannerDataUrl;
      };

      const bannerConfig = buildBannerConfig(profile, '/ortho-banner-template-premium.svg');
      const [logoDataUrl, bannerSourceDataUrl] = await Promise.all([
        resolvePdfLogoDataUrl(profile.logo),
        resolvePdfBannerDataUrl(bannerConfig.bannerImageUrl),
      ]);
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const originalSetFont = pdf.setFont.bind(pdf);
      pdf.setFont = ((fontName?: string, fontStyle?: string, fontWeight?: string) => {
        const resolvedFontName = fontName === ORTHO_PDF_FONT_FAMILY ? PDF_FALLBACK_FONT_FAMILY : fontName;
        return originalSetFont(resolvedFontName as any, fontStyle as any, fontWeight as any);
      }) as typeof pdf.setFont;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const left = 14;
      const right = 14;
      const top = 12;
      const bottom = 14;
      const contentWidth = pageWidth - left - right;
      const hasBannerSource = Boolean(bannerSourceDataUrl);
      const headerHeight = hasBannerSource ? 42 : 28;
      const infoTop = hasBannerSource ? headerHeight + 5 : 31;
      const otherBodyTop = hasBannerSource ? headerHeight + 7 : 38;
      const footerHeight = 10;
      const bodyBottom = pageHeight - bottom - footerHeight;
      const bannerDataUrl = bannerSourceDataUrl || null;
      const hasBanner = Boolean(bannerDataUrl);
      const pageTitle = selectedReportDocumentLabel || generatedDocument.title;
      const blocks = sanitizePdfBlocks(parseMarkdownForPdf(normalizedGeneratedDocumentContent), pageTitle);
      const lineHeight = (fontSize: number, factor = 1.38) => fontSize * 0.352777778 * factor;
      const notInformed = t('bureaucracyNotInformed');
      const ageLabel = language === 'en' ? 'Age' : language === 'es' ? 'Edad' : 'Idade';
      const sexLabel = language === 'en' ? 'Sex' : 'Sexo';
      const fullNameLabel = language === 'en' ? 'Full name' : language === 'es' ? 'Nombre completo' : 'Nome completo';
      const specialtyLabel = language === 'en' ? 'Specialty' : language === 'es' ? 'Especialidad' : 'Especialidade';
      const subspecialtyLabel = language === 'en' ? 'Subspecialty' : language === 'es' ? 'Subespecialidad' : 'Subespecialidade';
      const hospitalLabel = language === 'en' ? 'Hospital' : language === 'es' ? 'Hospital' : 'Hospital';
      const phoneLabel = language === 'en' ? 'Phone' : language === 'es' ? 'Teléfono' : 'Telefone';
      const emailLabel = language === 'en' ? 'Email' : language === 'es' ? 'Correo' : 'E-mail';
      const websiteLabel = language === 'en' ? 'Website' : language === 'es' ? 'Sitio web' : 'Site';
      const formatPdfValue = (value?: string | number | null, fallback = notInformed) => {
        if (value === null || value === undefined) return fallback;
        const normalized = normalizePdfText(String(value).trim());
        return normalized || fallback;
      };
      const headerTitlePrimary = formatPdfValue(bannerConfig.title, '');
      const headerTitleSecondary = formatPdfValue(bannerConfig.subtitle, '');
      const headerDisplayName = formatPdfValue(bannerConfig.displayName || bannerConfig.doctorName, '');
      const headerRole = formatPdfValue(bannerConfig.specialty || '');
      const headerCrm = formatPdfValue(
        bannerConfig.crm
          ? `CRM: ${bannerConfig.crm}${bannerConfig.crmState ? ` / ${bannerConfig.crmState}` : ''}`
          : bannerConfig.crmState
            ? `CRM / ${bannerConfig.crmState}`
            : ''
      );
      const headerHospital = formatPdfValue(bannerConfig.hospital || '');
      const headerPhone = formatPdfValue(bannerConfig.phone || '');
      const headerEmail = formatPdfValue(bannerConfig.email || '');
      const headerSite = formatPdfValue(bannerConfig.site || '');
      const mmToPt = (value: number) => value * 2.834645669;
      const ellipsizePdfText = (text: string, maxWidth: number, fontSize: number, fontStyle: 'normal' | 'bold' = 'normal') => {
        pdf.setFont('OrthoPrecisionSans', fontStyle);
        pdf.setFontSize(fontSize);
        const clean = formatPdfValue(text, '');
        if (!clean) return '';
        if (pdf.getTextWidth(clean) <= maxWidth) return clean;
        const ellipsis = '...';
        let output = clean;
        while (output.length > 0 && pdf.getTextWidth(`${output}${ellipsis}`) > maxWidth) {
          output = output.slice(0, -1);
        }
        return output ? `${output}${ellipsis}` : ellipsis;
      };
      const fitHeaderLines = (
        text: string,
        maxWidth: number,
        fontSize: number,
        maxLines: number,
        fontStyle: 'normal' | 'bold' = 'normal'
      ) => {
        const clean = formatPdfValue(text, '');
        if (!clean) return [];
        pdf.setFont('OrthoPrecisionSans', fontStyle);
        pdf.setFontSize(fontSize);
        let lines = pdf.splitTextToSize(clean, maxWidth) as string[];
        if (lines.length <= maxLines) return lines;
        lines = lines.slice(0, maxLines);
        lines[maxLines - 1] = ellipsizePdfText(lines[maxLines - 1], maxWidth, fontSize, fontStyle);
        return lines;
      };
      const formatAge = (currentPatient: Patient | null) => {
        const numericAge = typeof currentPatient?.age === 'number' ? currentPatient.age : Number(currentPatient?.age);
        if (!Number.isFinite(numericAge) || numericAge <= 0) return notInformed;
        return t('yearsOld', { count: numericAge });
      };
      const formatSex = (currentPatient: Patient | null) => {
        if (!currentPatient?.sex) return notInformed;
        if (currentPatient.sex === 'F') return t('female');
        if (currentPatient.sex === 'M') return t('male');
        return formatPdfValue(currentPatient.sex);
      };
      const formatCrmUf = () => {
        const crm = formatPdfValue(profile.crm, '').replace(/^CRM[:\s-]*/i, '').trim();
        const uf = formatPdfValue(profile.crmState, '').replace(/^\/+/g, '').trim();
        if (!crm && !uf) return notInformed;
        return [`CRM ${crm}`.trim(), uf ? `/${uf}` : ''].join(' ').replace(/\s+/g, ' ').trim();
      };
      const patientCardRows = [
        { label: fullNameLabel, value: formatPdfValue(patient?.name) },
        { label: ageLabel, value: formatAge(patient) },
        { label: sexLabel, value: formatSex(patient) },
        { label: language === 'en' ? 'Anatomy' : language === 'es' ? 'Anatomía' : 'Anatomia', value: formatPdfValue(patient?.anatomy) },
        { label: t('consultRecordPatientIdLabel'), value: formatPdfValue(patient?.id) },
      ];
      const doctorCardRows = [
        { label: fullNameLabel, value: formatPdfValue(profile.name) },
        { label: 'CRM / UF', value: formatCrmUf() },
        { label: 'RQE', value: formatPdfValue(profile.rqe) },
        { label: specialtyLabel, value: formatPdfValue(profile.specialty) },
        { label: hospitalLabel, value: formatPdfValue(profile.hospital) },
        { label: phoneLabel, value: formatPdfValue(profile.phone) },
        { label: emailLabel, value: formatPdfValue(profile.email) },
        { label: websiteLabel, value: formatPdfValue(profile.website) },
        { label: 'Ortho ID', value: formatPdfValue(profile.orthoId) },
      ];
      if (profile.subspecialty) {
        doctorCardRows.splice(4, 0, { label: subspecialtyLabel, value: formatPdfValue(profile.subspecialty) });
      }
      const documentStripRows = [
        { label: t('consultPdfDocumentType'), value: formatPdfValue(pageTitle) },
        { label: t('consultPdfIssuedAt'), value: `${formattedDate} • ${formattedTime}` },
      ];

      const cardPaddingX = 4;
      const cardLabelSize = 6.7;
      const cardValueSize = 8.4;
      const cardLineHeight = 3.9;
      const cardGap = 4;
      const cardWidth = (contentWidth - cardGap) / 2;
      const measureInfoCardHeight = (rows: Array<{ label: string; value: string }>) => {
        const valueWidth = cardWidth - cardPaddingX * 2;
        const headerHeightBlock = 8.1;
        const rowsHeight = rows.reduce((total, row) => {
          const wrappedValue = pdf.splitTextToSize(formatPdfValue(row.value), valueWidth);
          return total + 3.5 + wrappedValue.length * cardLineHeight + 1.7;
        }, 0);
        return Math.max(47, headerHeightBlock + rowsHeight + 1.8);
      };
      const measureStripHeight = () => {
        const columnCount = Math.max(1, documentStripRows.length);
        const columnWidth = contentWidth / columnCount;
        const maxValueLines = Math.max(
          ...documentStripRows.map((row) => pdf.splitTextToSize(formatPdfValue(row.value), columnWidth - 8).length),
          1
        );
        return Math.max(20, 9.2 + maxValueLines * 3.9 + 3.2);
      };
      const cardHeight = Math.max(
        measureInfoCardHeight(patientCardRows),
        measureInfoCardHeight(doctorCardRows)
      );
      const stripHeight = measureStripHeight();
      const stripTop = infoTop + cardHeight + 4;
      const firstBodyTop = stripTop + stripHeight + 6;
      const getPdfImageFormat = (dataUrl: string) => {
        if (/^data:image\/jpe?g/i.test(dataUrl)) return 'JPEG';
        if (/^data:image\/webp/i.test(dataUrl)) return 'WEBP';
        return 'PNG';
      };

      const drawContainedImage = (
        dataUrl: string,
        x: number,
        y: number,
        maxWidth: number,
        maxHeight: number
      ) => {
        const format = getPdfImageFormat(dataUrl);
        try {
          const imageProps = pdf.getImageProperties(dataUrl);
          const sourceWidth = Number(imageProps.width) || maxWidth;
          const sourceHeight = Number(imageProps.height) || maxHeight;
          const scale = Math.min(maxWidth / sourceWidth, maxHeight / sourceHeight);
          const width = sourceWidth * scale;
          const height = sourceHeight * scale;
          pdf.addImage(dataUrl, format, x + (maxWidth - width) / 2, y + (maxHeight - height) / 2, width, height);
          return true;
        } catch (error) {
          console.error('Failed to fit image in PDF:', error);
          try {
            pdf.addImage(dataUrl, format, x, y, maxWidth, maxHeight);
            return true;
          } catch (fallbackError) {
            console.error('Failed to draw fallback image in PDF:', fallbackError);
            return false;
          }
        }
      };

      const drawCoveredImage = (
        dataUrl: string,
        x: number,
        y: number,
        maxWidth: number,
        maxHeight: number
      ) => {
        const format = getPdfImageFormat(dataUrl);
        try {
          const imageProps = pdf.getImageProperties(dataUrl);
          const sourceWidth = Number(imageProps.width) || maxWidth;
          const sourceHeight = Number(imageProps.height) || maxHeight;
          const scale = Math.max(maxWidth / sourceWidth, maxHeight / sourceHeight);
          const width = sourceWidth * scale;
          const height = sourceHeight * scale;
          pdf.addImage(
            dataUrl,
            format,
            x - (width - maxWidth) / 2,
            y - (height - maxHeight) / 2,
            width,
            height
          );
          return true;
        } catch (error) {
          console.error('Failed to cover image in PDF:', error);
          try {
            pdf.addImage(dataUrl, format, x, y, maxWidth, maxHeight);
            return true;
          } catch (fallbackError) {
            console.error('Failed to draw fallback banner in PDF:', fallbackError);
            return false;
          }
        }
      };

      let currentPage = 1;
      let cursorY = firstBodyTop;

      const drawPageChrome = (isFirstPage: boolean) => {
        if (hasBanner && bannerDataUrl) {
          pdf.addImage(bannerDataUrl, 'PNG', 0, 0, pageWidth, headerHeight);
        } else {
          const bannerTop = 0;
          const bannerBottom = headerHeight;
          const bannerHeight = bannerBottom - bannerTop;

          pdf.setFillColor(9, 27, 72);
          pdf.rect(0, bannerTop, pageWidth, bannerHeight, 'F');
          pdf.setFillColor(14, 43, 108);
          pdf.rect(0, bannerTop, pageWidth * 0.65, bannerHeight, 'F');
          pdf.setFillColor(10, 38, 99);
          pdf.rect(pageWidth * 0.65, bannerTop, pageWidth * 0.35, bannerHeight, 'F');
          pdf.setFillColor(32, 112, 204);
          pdf.rect(0, bannerBottom - 1.2, pageWidth, 1.2, 'F');
        }

        const bannerLayout = getBannerLayoutMetrics(
          {
            ...bannerConfig,
            bannerImageUrl: bannerDataUrl || '',
          } as any,
          pageWidth,
          headerHeight
        );

        const parsedHeaderTextColor = (() => {
          const raw = String(bannerConfig.textColor || '').trim();
          const match = raw.match(/^#?([0-9a-fA-F]{6})$/);
          if (match) {
            const hex = match[1];
            return [
              Number.parseInt(hex.slice(0, 2), 16),
              Number.parseInt(hex.slice(2, 4), 16),
              Number.parseInt(hex.slice(4, 6), 16),
            ] as const;
          }
          return bannerConfig.backgroundStyle === 'light-clinical'
            ? ([15, 23, 42] as const)
            : ([255, 255, 255] as const);
        })();

        const headerLeftX = Math.max(5.5, bannerLayout.leftX);
        const headerLeftY = Math.max(5.9, bannerLayout.leftY);
        const headerRightAnchor = bannerLayout.rightX + bannerLayout.rightWidth;
        const headerRightY = Math.max(5.9, bannerLayout.rightY);
        const headerLeftMaxWidth = Math.max(52, bannerLayout.leftWidth);
        const headerRightMaxWidth = Math.max(42, bannerLayout.rightWidth);
        const titleFontSize = mmToPt(bannerLayout.titleFontSize);
        const subtitleFontSize = mmToPt(bannerLayout.subtitleFontSize);
        const rightHospitalFontSize = mmToPt(bannerLayout.displayNameFontSize * 1.08);
        const rightDoctorFontSize = mmToPt(bannerLayout.displayNameFontSize * 0.98);
        const rightLineFontSize = mmToPt(bannerLayout.lineFontSize * 0.96);

        pdf.setTextColor(...parsedHeaderTextColor);
        pdf.setFont('OrthoPrecisionSans', 'bold');
        pdf.setFontSize(titleFontSize);
        const leftTitleLines = fitHeaderLines(headerTitlePrimary || 'ORTHO PRECISION', headerLeftMaxWidth, titleFontSize, 2, 'bold');
        const leftTitleBaseline = headerLeftY + Math.max(4.0, bannerLayout.titleFontSize * 0.76);
        leftTitleLines.forEach((line, index) => {
          pdf.text(line, headerLeftX, leftTitleBaseline + index * Math.max(4.2, bannerLayout.titleLineHeight));
        });

        pdf.setFontSize(subtitleFontSize);
        pdf.text(
          ellipsizePdfText(headerTitleSecondary || 'MEDICAL CREDENTIAL', headerLeftMaxWidth, subtitleFontSize, 'bold'),
          headerLeftX,
          leftTitleBaseline + leftTitleLines.length * Math.max(4.0, bannerLayout.titleLineHeight) + Math.max(2.0, bannerLayout.subtitleGap)
        );

        const rightBaseline = headerRightY + Math.max(4.2, bannerLayout.displayNameFontSize * 0.78);
        pdf.setFont('OrthoPrecisionSans', 'bold');
        pdf.setFontSize(rightHospitalFontSize);
        pdf.text(
          ellipsizePdfText(headerHospital || 'Clínica / Hospital', headerRightMaxWidth, rightHospitalFontSize, 'bold'),
          headerRightAnchor,
          rightBaseline,
          { align: 'right' }
        );

        pdf.setFontSize(rightDoctorFontSize);
        pdf.text(
          ellipsizePdfText(headerDisplayName || headerRole || 'Médico Responsável', headerRightMaxWidth, rightDoctorFontSize, 'bold'),
          headerRightAnchor,
          rightBaseline + Math.max(3.8, bannerLayout.lineGap * 1.02),
          { align: 'right' }
        );

        pdf.setFont('OrthoPrecisionSans', 'normal');
        pdf.setFontSize(rightLineFontSize);
        const rightLines = [
          headerPhone ? `Telefone: ${headerPhone}` : '',
          headerEmail ? `E-mail: ${headerEmail}` : '',
          headerSite ? `Site: ${headerSite}` : '',
        ].filter(Boolean);
        rightLines.forEach((line, index) => {
          pdf.text(
            ellipsizePdfText(line, headerRightMaxWidth, rightLineFontSize, 'normal'),
            headerRightAnchor,
            rightBaseline + Math.max(6.4, bannerLayout.lineGap * 1.32) + index * Math.max(3.2, bannerLayout.lineGap * 1.02),
            { align: 'right' }
          );
        });

        if (isFirstPage) {
          pdf.setDrawColor(226, 232, 240);
          pdf.setFillColor(255, 255, 255);
          const drawInfoCard = (x: number, y: number, width: number, title: string, rows: Array<{ label: string; value: string }>, accentColor: [number, number, number]) => {
            pdf.setDrawColor(226, 232, 240);
            pdf.setFillColor(255, 255, 255);
            pdf.roundedRect(x, y, width, cardHeight, 3, 3, 'FD');
            pdf.setFillColor(accentColor[0], accentColor[1], accentColor[2]);
            pdf.roundedRect(x, y, width, 6.2, 3, 3, 'F');

            pdf.setFont('OrthoPrecisionSans', 'bold');
            pdf.setFontSize(7.4);
            pdf.setTextColor(255, 255, 255);
            pdf.setCharSpace(0.2);
            pdf.text(title.toUpperCase(), x + cardPaddingX, y + 4.15);
            pdf.setCharSpace(0);
            pdf.setDrawColor(226, 232, 240);
            pdf.setLineWidth(0.2);
            pdf.line(x + cardPaddingX, y + 8.1, x + width - cardPaddingX, y + 8.1);

            let rowY = y + 11.4;
            const valueWidth = width - cardPaddingX * 2;

            rows.forEach((row) => {
              pdf.setFont('OrthoPrecisionSans', 'bold');
              pdf.setFontSize(cardLabelSize);
              pdf.setTextColor(100, 116, 139);
              pdf.text(row.label.toUpperCase(), x + cardPaddingX, rowY);

              const wrappedValue = pdf.splitTextToSize(formatPdfValue(row.value), valueWidth);
              pdf.setFont('OrthoPrecisionSans', 'normal');
              pdf.setFontSize(cardValueSize);
              pdf.setTextColor(15, 23, 42);
              wrappedValue.forEach((line: string, index: number) => {
                pdf.text(line, x + cardPaddingX, rowY + 3.5 + index * cardLineHeight);
              });

              rowY += 3.5 + wrappedValue.length * cardLineHeight + 1.7;
            });
          };

          const drawDocumentStrip = (x: number, y: number, width: number) => {
            pdf.setDrawColor(226, 232, 240);
            pdf.setFillColor(248, 250, 252);
            pdf.roundedRect(x, y, width, stripHeight, 3, 3, 'FD');
            pdf.setDrawColor(226, 232, 240);
            pdf.setLineWidth(0.2);

            const columnCount = Math.max(1, documentStripRows.length);
            const columnWidth = width / columnCount;
            for (let column = 1; column < columnCount; column += 1) {
              pdf.line(x + columnWidth * column, y + 2.4, x + columnWidth * column, y + stripHeight - 2.4);
            }

            documentStripRows.forEach((row, index) => {
              const cellX = x + index * columnWidth + 4;
              const valueText = formatPdfValue(row.value);
              pdf.setFont('OrthoPrecisionSans', 'bold');
              pdf.setFontSize(6.6);
              pdf.setTextColor(100, 116, 139);
              pdf.text(row.label.toUpperCase(), cellX, y + 5.4);

              const wrappedValue = pdf.splitTextToSize(valueText, columnWidth - 8);
              pdf.setFont('OrthoPrecisionSans', 'normal');
              pdf.setFontSize(8.1);
              pdf.setTextColor(15, 23, 42);
              wrappedValue.forEach((line: string, lineIndex: number) => {
                pdf.text(line, cellX, y + 9.2 + lineIndex * 3.9);
              });
            });
          };

          drawInfoCard(left, infoTop, cardWidth, language === 'en' ? 'PATIENT' : 'PACIENTE', patientCardRows, [37, 99, 235]);
          drawInfoCard(left + cardWidth + cardGap, infoTop, cardWidth, t('consultPdfPhysician').toUpperCase(), doctorCardRows, [37, 99, 235]);
          drawDocumentStrip(left, stripTop, contentWidth);
          cursorY = firstBodyTop;
        } else {
          cursorY = otherBodyTop;
        }
      };

      const addNewPage = () => {
        pdf.addPage();
        currentPage += 1;
        drawPageChrome(false);
      };

      const ensureSpace = (needed: number) => {
        if (cursorY + needed > bodyBottom) {
          addNewPage();
        }
      };

      const drawParagraph = (text: string, fontSize = 10.2, color: [number, number, number] = [15, 23, 42]) => {
        const lines = pdf.splitTextToSize(text, contentWidth);
        const lh = lineHeight(fontSize);
        pdf.setFont('OrthoPrecisionSans', 'normal');
        pdf.setFontSize(fontSize);
        pdf.setTextColor(color[0], color[1], color[2]);

        for (const line of lines) {
          ensureSpace(lh);
          pdf.text(line, left, cursorY);
          cursorY += lh;
        }

        cursorY += 2.5;
      };

      const drawHeading = (text: string, level: 1 | 2 | 3) => {
        const fontSize = level === 1 ? 13.2 : level === 2 ? 11.4 : 10.2;
        const extraBottom = level === 1 ? 5 : 4.2;
        const lines = pdf.splitTextToSize(text, contentWidth);
        const lh = lineHeight(fontSize, 1.12);
        ensureSpace(lines.length * lh + extraBottom + 4);
        pdf.setFont('OrthoPrecisionSans', 'bold');
        pdf.setFontSize(fontSize);
        pdf.setTextColor(level === 1 ? 29 : 37, level === 1 ? 78 : 99, level === 1 ? 216 : 235);
        lines.forEach((line: string, index: number) => {
          pdf.text(line, left, cursorY + index * lh);
        });
        cursorY += lines.length * lh + 2.2;
        pdf.setDrawColor(level === 1 ? 59 : 203, level === 1 ? 130 : 213, level === 1 ? 246 : 225);
        pdf.setLineWidth(level === 1 ? 0.55 : 0.3);
        pdf.line(left, cursorY, left + Math.min(80, contentWidth), cursorY);
        cursorY += extraBottom;
      };

      const drawListItem = (text: string, index: number, ordered: boolean) => {
        const prefix = ordered ? `${index + 1}.` : '•';
        const fontSize = 9.8;
        const indent = 5.8;
        const bulletWidth = pdf.getTextWidth(prefix) + 1.5;
        const availableWidth = contentWidth - indent - bulletWidth;
        const lines = pdf.splitTextToSize(text, availableWidth);
        const lh = lineHeight(fontSize);
        ensureSpace(lines.length * lh + 2.4);

        pdf.setFont('OrthoPrecisionSans', 'bold');
        pdf.setFontSize(fontSize);
        pdf.setTextColor(37, 99, 235);
        pdf.text(prefix, left + indent, cursorY);

        pdf.setFont('OrthoPrecisionSans', 'normal');
        pdf.setTextColor(15, 23, 42);
        lines.forEach((line, lineIndex) => {
          if (lineIndex > 0) {
            ensureSpace(lh);
          }
          pdf.text(line, left + indent + bulletWidth, cursorY);
          cursorY += lh;
        });

        cursorY += 2.2;
      };

      const drawSpacer = () => {
        cursorY += 3.6;
      };

      drawPageChrome(true);

      for (const block of blocks) {
        if (block.kind === 'spacer') {
          drawSpacer();
          continue;
        }

        if (block.kind === 'heading') {
          drawHeading(block.text, block.level);
          continue;
        }

        if (block.kind === 'paragraph') {
          if (block.text) {
            drawParagraph(block.text);
          }
          continue;
        }

        if (block.kind === 'list') {
          block.items.forEach((item, index) => drawListItem(item, index, block.ordered));
        }
      }

      const signatureBlockHeight = profile.signature ? 32 : 24;
      const verificationBlockHeight = 44;
      ensureSpace(signatureBlockHeight + verificationBlockHeight + 8);

      if (profile.signature) {
        const signatureImage = await fetchImageAsDataUrl(profile.signature);
        if (signatureImage) {
          try {
            const imageFormat = signatureImage.startsWith('data:image/jpeg') || signatureImage.startsWith('data:image/jpg')
              ? 'JPEG'
              : 'PNG';
            pdf.addImage(signatureImage, imageFormat, pageWidth - right - 52, cursorY - 1, 52, 18);
            cursorY += 20;
          } catch (error) {
            console.error('Failed to add signature image:', error);
            pdf.setDrawColor(15, 23, 42);
            pdf.line(pageWidth - right - 58, cursorY + 11, pageWidth - right, cursorY + 11);
            cursorY += 14;
          }
        } else {
          pdf.setDrawColor(15, 23, 42);
          pdf.line(pageWidth - right - 58, cursorY + 11, pageWidth - right, cursorY + 11);
          cursorY += 14;
        }
      } else {
        pdf.setDrawColor(15, 23, 42);
        pdf.line(pageWidth - right - 58, cursorY + 11, pageWidth - right, cursorY + 11);
        cursorY += 14;
      }

      pdf.setFont('OrthoPrecisionSans', 'bold');
      pdf.setFontSize(10.5);
      pdf.setTextColor(15, 23, 42);
      pdf.text(formatPdfValue(profile.name), pageWidth - right, cursorY, { align: 'right' });

      pdf.setFont('OrthoPrecisionSans', 'normal');
      pdf.setFontSize(8.5);
      pdf.setTextColor(71, 85, 105);
      pdf.text(`CRM / UF: ${formatCrmUf()}`, pageWidth - right, cursorY + 4.8, { align: 'right' });
      pdf.text(`RQE: ${formatPdfValue(profile.rqe)}`, pageWidth - right, cursorY + 9.1, { align: 'right' });
      pdf.text(`Especialidade: ${formatPdfValue(profile.specialty)}`, pageWidth - right, cursorY + 13.4, { align: 'right' });
      if (profile.subspecialty) {
        pdf.text(`Subespecialidade: ${formatPdfValue(profile.subspecialty)}`, pageWidth - right, cursorY + 17.7, { align: 'right' });
      }
      if (profile.hospital) {
        pdf.text(
          `Hospital: ${formatPdfValue(profile.hospital)}`,
          pageWidth - right,
          cursorY + (profile.subspecialty ? 22.0 : 17.7),
          { align: 'right' }
        );
      }

      cursorY += profile.hospital ? (profile.subspecialty ? 28.0 : 24.4) : (profile.subspecialty ? 23.4 : 20.6);

      const verificationTop = cursorY + 2;
      const verificationLabelWidth = contentWidth - 34;
      const verificationUrlText = verificationBundle.verificationUrl.replace(/^https?:\/\//i, '');
      const wrappedVerificationUrl = pdf.splitTextToSize(verificationUrlText, verificationLabelWidth);
      const verificationCardHeight = Math.max(34, 18 + wrappedVerificationUrl.length * 3.1);

      pdf.setDrawColor(226, 232, 240);
      pdf.setFillColor(248, 250, 252);
      pdf.roundedRect(left, verificationTop, contentWidth, verificationCardHeight, 3, 3, 'FD');
      pdf.setFillColor(37, 99, 235);
      pdf.roundedRect(left, verificationTop, contentWidth, 6.2, 3, 3, 'F');
      pdf.setFont('OrthoPrecisionSans', 'bold');
      pdf.setFontSize(7.4);
      pdf.setTextColor(255, 255, 255);
      pdf.text(t('consultPdfVerificationTitle').toUpperCase(), left + cardPaddingX, verificationTop + 4.15);

      if (qrDataUrl) {
        try {
          pdf.addImage(qrDataUrl, 'PNG', left + 4, verificationTop + 8.5, 22, 22);
        } catch (error) {
          console.error('Failed to add verification QR in PDF:', error);
        }
      }

      const verificationTextX = left + 31;
      let verificationTextY = verificationTop + 11;
      pdf.setFont('OrthoPrecisionSans', 'bold');
      pdf.setFontSize(8.4);
      pdf.setTextColor(15, 23, 42);
      pdf.text(`${t('consultPdfVerificationCode')}: ${verificationBundle.verificationCode}`, verificationTextX, verificationTextY);

      verificationTextY += 4.4;
      pdf.setFont('OrthoPrecisionSans', 'normal');
      pdf.setFontSize(7.6);
      pdf.setTextColor(71, 85, 105);
      const verificationNote = pdf.splitTextToSize(t('consultPdfVerificationText'), verificationLabelWidth);
      verificationNote.forEach((line: string, index: number) => {
        pdf.text(line, verificationTextX, verificationTextY + index * 3.2);
      });

      const urlStartY = verificationTextY + verificationNote.length * 3.2 + 1.8;
      pdf.setFont('OrthoPrecisionSans', 'bold');
      pdf.setFontSize(7.2);
      pdf.setTextColor(37, 99, 235);
      pdf.text(t('consultPdfVerificationPortal'), verificationTextX, urlStartY);
      pdf.setFont('OrthoPrecisionSans', 'normal');
      pdf.setFontSize(7.1);
      pdf.setTextColor(71, 85, 105);
      wrappedVerificationUrl.forEach((line: string, index: number) => {
        pdf.text(line, verificationTextX, urlStartY + 3.3 + index * 3.1);
      });
      cursorY = verificationTop + verificationCardHeight;

      const totalPages = pdf.getNumberOfPages();
      for (let page = 1; page <= totalPages; page++) {
        pdf.setPage(page);
        pdf.setDrawColor(226, 232, 240);
        pdf.setLineWidth(0.2);
        pdf.line(left, pageHeight - 11.4, pageWidth - right, pageHeight - 11.4);
        pdf.setFont('OrthoPrecisionSans', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(100, 116, 139);
        pdf.text(formatPdfValue(profile.headerName || profile.hospital || 'OrthoPrecision'), left, pageHeight - 6.8);
        pdf.text(`Page ${page} / ${totalPages}`, pageWidth - right, pageHeight - 6.8, { align: 'right' });
      }

      const attachmentFileName = `${fileNameBase || 'orthoprecision_report'}.pdf`;
      const pdfBlob = pdf.output('blob');

      if (patient) {
        const attachmentKey = [
          patient.id,
          selectedReportDocumentType || 'technical_report',
          selectedReportDocumentLabel || generatedDocument.title,
          normalizedGeneratedDocumentContent,
          documentClinicalOpinion.trim(),
        ].join('|');

        if (!savedComplementaryPdfKeysRef.current.has(attachmentKey)) {
          try {
            const attachmentFile = new File([pdfBlob], attachmentFileName, {
              type: 'application/pdf',
            });

            const uploadedPdf = await uploadPatientFile({
              uid,
              patientId: patient.id,
              file: attachmentFile,
            });

            const currentFiles = Array.isArray((patient as any).patientFiles)
              ? [...((patient as any).patientFiles as PatientFile[])]
              : [];
            const nextFiles = [
              uploadedPdf,
              ...currentFiles.filter(
                (candidate) =>
                  candidate.url !== uploadedPdf.url &&
                  candidate.storagePath !== uploadedPdf.storagePath &&
                  candidate.name !== uploadedPdf.name
              ),
            ];

            await updatePatient(patient.id, {
              patientFiles: nextFiles,
            } as any);

            setActivePatient({
              ...patient,
              patientFiles: nextFiles,
            } as Patient);
            savedComplementaryPdfKeysRef.current.add(attachmentKey);
          } catch (attachmentError) {
            console.error('Falha ao anexar PDF complementar ao paciente:', attachmentError);
          }
        }
      }

      const downloadUrl = URL.createObjectURL(pdfBlob);
      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = attachmentFileName;
      downloadLink.click();
      setTimeout(() => URL.revokeObjectURL(downloadUrl), 1000);
    } catch (error) {
      console.error(error);
      setPdfExportError(error instanceof Error ? error.message : t('consultPdfExportError'));
    } finally {
      setIsExportingPdf(false);
    }
  };

  // Determine which image to show in standard view
  const activeImage = selectedDbItem 
    ? (customImages[selectedDbItem.id] || selectedDbItem.imagemReferencia)
    : null;

  const isCustomImage = selectedDbItem && !!customImages[selectedDbItem.id];

  return (
    <div className={`h-full flex ${isReportAnalysisModule ? 'flex-col' : 'flex-col md:flex-row'} gap-6 animate-in fade-in duration-500`}>
      {isReportAnalysisModule && (
        <div className="glass rounded-2xl border border-purple-500/20 bg-slate-900/40 p-5 shadow-xl">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-purple-500/20 bg-purple-500/10 text-purple-400">
              <ScanText className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{t('reportAnalysisTitle')}</h2>
              <p className="mt-1 text-sm text-slate-400">{t('reportAnalysisSubtitle')}</p>
            </div>
          </div>
        </div>
      )}

      {showSidebar && (
        <div className="w-full md:w-[340px] xl:w-[360px] flex flex-col gap-4 h-[calc(100vh-140px)] md:h-auto">
          <div className="glass flex flex-col h-full rounded-[28px] border border-[color:var(--op-border)] overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.35)]">
          
            {/* Sidebar Header */}
            <div className="p-5 border-b border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/85 backdrop-blur-md sticky top-0 z-10 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-[color:var(--op-text)] uppercase tracking-widest flex items-center gap-2">
                  <Database className="w-4 h-4 text-blue-400" /> {t('consultLibraryTitle')}
                </h3>
                <span className="text-[10px] bg-cyan-500/10 text-cyan-300 px-2 py-0.5 rounded-full border border-[color:var(--op-border-strong)] shadow-[0_0_18px_rgba(34,211,238,0.08)]">
                  {filteredDb.length}
                </span>
              </div>
              
              {/* Sidebar Search Input */}
              <div className="relative group">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[color:var(--op-text-muted)] group-focus-within:text-cyan-300 transition-colors" />
                <input 
                  type="text"
                  value={localFilter}
                  onChange={(e) => setLocalFilter(e.target.value)}
                  placeholder={t("filterProtocolsPlaceholder")}
                  className="w-full rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/80 pl-9 pr-8 py-3 text-xs text-[color:var(--op-text)] placeholder:text-[color:var(--op-text-muted)]/60 outline-none transition-all focus:border-[color:var(--op-border-strong)] focus:shadow-[0_0_0_2px_rgba(34,211,238,0.1)] focus:bg-white/[0.03]"
                />
                {localFilter && (
                  <button 
                    onClick={() => setLocalFilter('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--op-text-muted)] hover:text-[color:var(--op-text)]"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            </div>

            {/* List Area */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2 custom-scrollbar bg-[color:var(--op-bg-deep)]/20">
              {filteredDb.length === 0 ? (
                 <div className="p-8 text-center text-[color:var(--op-text-muted)] flex flex-col items-center gap-3">
                    <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] shadow-[0_0_22px_rgba(34,211,238,0.06)]">
                      <Database className="w-6 h-6 text-cyan-300/70" />
                    </div>
                    <span className="text-xs">{t('consultNoProtocolsFound')}</span>
                 </div>
              ) : (
                filteredDb.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleSelectDbItem(item)}
                    className={`w-full text-left p-3 rounded-xl border transition-all flex items-center gap-3 group relative overflow-hidden ${
                      selectedDbItem?.id === item.id && mode === 'search'
                        ? 'bg-cyan-500/10 border-[color:var(--op-border-strong)] shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_0_18px_rgba(34,211,238,0.08)]' 
                        : 'bg-[color:var(--op-card-bg-soft)] border-[color:var(--op-border)] hover:bg-white/[0.03] hover:border-[color:var(--op-border-strong)]'
                    }`}
                  >
                    {selectedDbItem?.id === item.id && mode === 'search' && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-cyan-300" />
                    )}
                    
                    <div className={`p-2 rounded-lg ${selectedDbItem?.id === item.id ? 'bg-cyan-500/15' : 'bg-[color:var(--op-bg-deep)]/80'}`}>
                      {getCategoryIcon(item.taxonomy)}
                    </div>

                    <div className="flex-1 overflow-hidden">
                      <div className={`text-xs font-bold truncate ${selectedDbItem?.id === item.id && mode === 'search' ? 'text-[color:var(--op-text)]' : 'text-[color:var(--op-text-muted)] group-hover:text-[color:var(--op-text)]'}`}>
                        {item.nome}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                         {item.taxonomy.anatomy.slice(0, 2).map((anat, i) => (
                           <span key={i} className="text-[9px] px-1.5 py-px rounded bg-[color:var(--op-bg-deep)]/80 border border-[color:var(--op-border)] text-[color:var(--op-text-muted)] uppercase tracking-wide truncate max-w-[80px]">
                             {anat}
                           </span>
                         ))}
                         {customImages[item.id] && (
                           <span className="text-[9px] px-1.5 py-px rounded bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 uppercase tracking-wide">
                             Foto
                           </span>
                         )}
                      </div>
                    </div>

                    <ChevronRight className={`w-3 h-3 shrink-0 transition-transform text-[color:var(--op-text-muted)] ${selectedDbItem?.id === item.id ? 'translate-x-0 text-cyan-300' : '-translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100'}`} />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* MAIN CONTENT AREA */}
      <div className="flex-1 flex flex-col h-full overflow-hidden">
        {moduleView === 'pathologies' && (
          <div className="mb-4 rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-6 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_18px_45px_rgba(0,0,0,0.22)]">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-cyan-300">{t('consultPathologyHeaderKicker')}</p>
                <h2 className="mt-1 text-2xl font-bold text-[color:var(--op-text)]">{t('consultPathologyHeaderTitle')}</h2>
              </div>
              <span className="shrink-0 rounded-full border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--op-text-muted)]">
                {filteredDb.length} {t('consultPathologyProtocolsCount')}
              </span>
            </div>
          </div>
        )}
        {moduleView === 'combined' && mode === 'search' && (
          <div className="mb-4 rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-6 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_18px_45px_rgba(0,0,0,0.22)]">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-cyan-300">{t('consultPathologyHeaderKicker')}</p>
                <h2 className="mt-1 text-2xl font-bold text-[color:var(--op-text)]">{t('consultPathologyHeaderTitle')}</h2>
              </div>
              <span className="shrink-0 rounded-full border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[color:var(--op-text-muted)]">
                {filteredDb.length} {t('consultPathologyProtocolsCount')}
              </span>
            </div>
          </div>
        )}
        
        {/* Navigation Tabs (Search vs Report) */}
        {showModeTabs && (
        <div className="flex gap-2 mb-4 shrink-0">
            <button 
                onClick={() => setMode('search')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold uppercase text-xs transition-all border ${mode === 'search' ? 'bg-cyan-500/15 border-[color:var(--op-border-strong)] text-white shadow-[0_0_18px_rgba(34,211,238,0.08)]' : 'bg-[color:var(--op-card-bg-soft)] border-[color:var(--op-border)] text-[color:var(--op-text-muted)] hover:bg-white/[0.04] hover:text-[color:var(--op-text)]'}`}
            >
                <Search className="w-4 h-4" /> {t('consultProtocolsAiTab')}
            </button>
            <button 
                onClick={() => setMode('report')}
                className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold uppercase text-xs transition-all border ${mode === 'report' ? 'bg-cyan-500/15 border-[color:var(--op-border-strong)] text-white shadow-[0_0_18px_rgba(34,211,238,0.08)]' : 'bg-[color:var(--op-card-bg-soft)] border-[color:var(--op-border)] text-[color:var(--op-text-muted)] hover:bg-white/[0.04] hover:text-[color:var(--op-text)]'}`}
            >
                <ScanText className="w-4 h-4" /> {t('consultReportAnalysisTab')}
            </button>
        </div>
        )}
 
        {/* Content Display */}
        <div className="flex-1 glass rounded-3xl p-6 border border-[color:var(--op-border)] overflow-y-auto custom-scrollbar relative min-h-[400px] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
          
          {mode === 'search' ? (
            <>
                {/* Search Bar */}
                <form onSubmit={handleSearch} className="relative mb-6 shrink-0 z-10">
                    <div className="absolute inset-0 rounded-full bg-cyan-500/5 blur-xl" />
                    <div className="relative flex items-center rounded-[22px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-2 transition-colors shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_18px_45px_rgba(0,0,0,0.18)] focus-within:border-[color:var(--op-border-strong)] focus-within:shadow-[0_0_0_2px_rgba(34,211,238,0.1)]">
                        <Search className="ml-3 h-5 w-5 text-[color:var(--op-text-muted)]" />
                        <input 
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("generativeAiPlaceholder")}
                        className="w-full border-none bg-transparent px-4 py-3 text-[color:var(--op-text)] outline-none placeholder:text-[color:var(--op-text-muted)]/60 focus:ring-0"
                        />
                        <button 
                        type="submit" 
                        disabled={isAnalyzing || !searchQuery}
                        className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-500 px-6 py-3 text-xs font-bold uppercase tracking-widest text-white shadow-[0_12px_32px_rgba(34,211,238,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(34,211,238,0.22)] disabled:opacity-50 flex items-center"
                        >
                        {isAnalyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {t('analyzeBtn')}
                        </button>
                    </div>
                </form>

                {isAnalyzing ? (
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 text-slate-500">
                    <BrainCircuit className="w-16 h-16 text-blue-500 animate-pulse" />
                    <div className="flex flex-col items-center gap-1">
                        <p className="text-sm font-bold text-white animate-pulse uppercase tracking-widest">{t('consultAnalyzingClinicalCases')}</p>
                        <p className="text-xs">{t('consultKnowledgeBaseLoading')}</p>
                    </div>
                    </div>
                ) : selectedDbItem ? (
                    // STATIC DB VIEW
                    <div className="space-y-8 animate-in slide-in-from-bottom-4 pb-10">
                    <div className="flex flex-col md:flex-row items-start justify-between border-b border-[color:var(--op-border)] pb-6 gap-6">
                        <div className="flex-1">
                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/10 text-cyan-300 border border-[color:var(--op-border-strong)] uppercase tracking-widest flex items-center gap-1">
                                <Database className="w-3 h-3" /> {t('consultInstitutionalProtocol')}
                            </span>
                            {selectedDbItem.cid10 && <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[color:var(--op-bg-deep)]/80 text-[color:var(--op-text-muted)] border border-[color:var(--op-border)]">CID-10: {selectedDbItem.cid10}</span>}
                        </div>
                        <h2 className="text-4xl font-bold text-[color:var(--op-text)] mb-4 tracking-tight">{selectedDbItem.nome}</h2>
                        <div className="rounded-r-xl border-l-4 border-cyan-400/40 bg-[color:var(--op-card-bg-soft)] p-4">
                            <p className="text-sm leading-relaxed text-[color:var(--op-text-muted)]">{selectedDbItem.resumo}</p>
                        </div>
                        </div>
                        
                        {/* Image Container */}
                        <div className="relative w-full md:w-48 aspect-square shrink-0 group">
                        <div className="w-full h-full rounded-2xl border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)] overflow-hidden shadow-[0_24px_70px_rgba(0,0,0,0.35)] relative">
                            {activeImage && !imageError ? (
                                <img 
                                    src={activeImage} 
                                    alt="Ref" 
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" 
                                    onError={() => setImageError(true)}
                                />
                            ) : (
                                <div className="flex flex-col items-center justify-center h-full text-[color:var(--op-text-muted)] gap-2 p-2 text-center bg-[color:var(--op-bg-deep)]">
                                    <ImageOff className="w-8 h-8 opacity-30 text-cyan-300" />
                                    <span className="text-[9px] font-bold uppercase tracking-widest opacity-50">{t('consultOfflineImage')}</span>
                                </div>
                            )}
                            
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-60 group-hover:opacity-40 transition-opacity pointer-events-none" />
                            
                            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity bg-black/40 backdrop-blur-sm gap-2">
                            <input 
                                type="file" 
                                ref={fileInputRef}
                                onChange={handleImageUpload}
                                accept="image/*"
                                className="hidden" 
                            />
                            <button 
                                onClick={() => fileInputRef.current?.click()}
                                className="p-3 bg-white/10 hover:bg-cyan-500 text-white rounded-full backdrop-blur-md border border-white/20 transition-all shadow-lg"
                                title={t('consultUploadPhoto')}
                            >
                                <Camera className="w-5 h-5" />
                            </button>
                            {isCustomImage && (
                                <button 
                                onClick={handleRemoveCustomImage}
                                className="p-3 bg-white/10 hover:bg-red-600 text-white rounded-full backdrop-blur-md border border-white/20 transition-all shadow-lg"
                                title={t('consultRemoveCustomPhoto')}
                                >
                                <Trash2 className="w-5 h-5" />
                                </button>
                            )}
                            </div>
                        </div>
                        
                        {isCustomImage && (
                            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 bg-cyan-500 text-white text-[9px] font-bold px-2 py-0.5 rounded-full shadow-lg border border-cyan-300 z-10">
                            {t('consultCustomImage')}
                            </div>
                        )}
                        </div>
                    </div>

                    {/* ... rest of static DB view ... */}
                    {renderPathologyActions(true, 'consultSaveToRecord')}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        {/* Classificações */}
                        <div className="space-y-4">
                            <h3 className="text-xs font-bold text-[color:var(--op-text-muted)] flex items-center gap-2 uppercase tracking-[0.2em] border-b border-[color:var(--op-border)] pb-2">
                            <Layers className="w-4 h-4 text-cyan-300" /> {t('consultClassifications')}
                            </h3>
                            <div className="space-y-3">
                            {selectedDbItem.classificacaoDetalhada.map((cls, idx) => (
                                <div key={idx} className="rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-5 transition-all hover:-translate-y-0.5 hover:border-[color:var(--op-border-strong)] hover:shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_18px_45px_rgba(0,0,0,0.14)]">
                                    <div className="mb-3 flex items-center gap-2 text-sm font-bold text-[color:var(--op-text)]">
                                        <div className="h-1.5 w-1.5 rounded-full bg-cyan-300" />
                                        {cls.nome}
                                    </div>
                                    <ul className="space-y-3">
                                        {cls.tipos.map((t, i) => (
                                        <li key={i} className="flex flex-col gap-1 border-l border-[color:var(--op-border)] pl-4 text-xs text-[color:var(--op-text-muted)]">
                                            <span className="font-bold text-[color:var(--op-text)]">{t.nome}</span>
                                            <span className="leading-relaxed opacity-80">{t.descricao}</span>
                                        </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                            </div>
                        </div>

                        {/* Conduta */}
                        <div className="space-y-4">
                            <h3 className="text-xs font-bold text-[color:var(--op-text-muted)] flex items-center gap-2 uppercase tracking-[0.2em] border-b border-[color:var(--op-border)] pb-2">
                            <Stethoscope className="w-4 h-4 text-cyan-300" /> {t('consultTherapeuticManagement')}
                            </h3>
                            <div className="overflow-hidden rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)]">
                            {selectedDbItem.condutaConservadora && (
                                <div className="border-b border-[color:var(--op-border)] p-5">
                                    <span className="mb-2 block flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-emerald-300">
                                        <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> {t('consultConservative')}
                                    </span>
                                    <p className="text-sm leading-relaxed text-[color:var(--op-text-muted)]">{selectedDbItem.condutaConservadora}</p>
                                </div>
                            )}
                            {selectedDbItem.condutaCirurgica && (
                                <div className="bg-red-500/5 p-5">
                                    <span className="mb-2 block flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider text-red-300">
                                         <Activity className="w-3 h-3" /> {t('consultSurgical')}
                                    </span>
                                    <p className="text-sm leading-relaxed text-[color:var(--op-text-muted)]">{selectedDbItem.condutaCirurgica}</p>
                                </div>
                            )}
                            </div>
                        </div>
                    </div>

                    {selectedDbItem.checklistMaterial && (
                        <div className="relative overflow-hidden rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-6">
                        <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-cyan-500/10 blur-2xl" />
                        <h3 className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-[color:var(--op-text-muted)]">
                            <AlertTriangle className="w-4 h-4" /> {t('consultMaterialsChecklist')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {selectedDbItem.checklistMaterial.map((mat, i) => (
                                <div key={i} className="flex items-center gap-3 rounded-lg border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/80 p-3 text-xs text-[color:var(--op-text)]">
                                <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-cyan-300" /> 
                                <span className="font-medium">{mat}</span>
                                </div>
                            ))}
                        </div>
                        </div>
                    )}
                    </div>
                ) : aiResult ? (
                    // AI RESULT VIEW
                    <div className="relative space-y-8 animate-in slide-in-from-bottom-4">
                    {resultActionToast && (
                      <div
                        role="status"
                        aria-live="polite"
                        className={`absolute right-0 top-0 z-20 flex items-center gap-2 rounded-2xl border px-4 py-3 text-xs font-semibold shadow-[0_18px_45px_rgba(0,0,0,0.22)] ${
                          resultActionToast.tone === 'error'
                            ? 'border-red-500/30 bg-red-500/12 text-red-200'
                            : 'border-emerald-500/30 bg-emerald-500/12 text-emerald-200'
                        }`}
                      >
                        <Check className="h-4 w-4" />
                        <span>{resultActionToast.message}</span>
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-4 border-b border-[color:var(--op-border)] pb-6">
                        <div>
                        <div className="flex items-center gap-2 mb-2">
                            <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-cyan-500/10 text-cyan-300 border border-[color:var(--op-border-strong)] uppercase flex items-center gap-1">
                                <Sparkles className="w-3 h-3" /> {t('consultGenerativeAnalysis')}
                            </span>
                        </div>
                        <h2 className="mb-2 text-3xl font-bold text-[color:var(--op-text)]">{aiResult.nome}</h2>
                        </div>
                        {renderPathologyActions(true)}
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:gap-5">
                        {pathologyAiCards.map((card) => (
                          <PathologyInfoCard
                            key={card.key}
                            title={card.title}
                            value={card.value}
                            icon={card.icon}
                            tone={card.tone}
                          />
                        ))}
                    </div>
                    {typeof document !== 'undefined' && createPortal(
                      <div className="consult-pathology-print-root hidden">
                        <div className="consult-pathology-print-page">
                          <header className="consult-pathology-print-header print-no-break">
                            <p className="consult-pathology-print-kicker">{t('consultPathologyPrintHeader')}</p>
                            <div className="consult-pathology-print-header-row">
                              <div>
                                <h1 className="consult-pathology-print-title">{aiResult.nome}</h1>
                                <p className="consult-pathology-print-subtitle">{t('consultGenerativeAnalysis')}</p>
                              </div>
                              <div className="consult-pathology-print-meta">
                                <div>{t('consultPathologyPrintDate')}: {formatPathologyDate(new Date())}</div>
                                <div>{t('consultPathologyPrintIdentification')}: OrthoPrecision WEB</div>
                              </div>
                            </div>
                          </header>

                          <main className="consult-pathology-print-content">
                            {pathologyLines(aiResult.classificacao).length > 0 && (
                              <section className="consult-pathology-print-card print-no-break">
                                <h2>{t('consultClassification')}</h2>
                                <ul>
                                  {pathologyLines(aiResult.classificacao).map((item, index) => (
                                    <li key={`print-classification-${index}`}>{normalizeSharedText(item)}</li>
                                  ))}
                                </ul>
                              </section>
                            )}

                            {pathologyLines(aiResult.exameFisico).length > 0 && (
                              <section className="consult-pathology-print-card print-no-break">
                                <h2>{t('consultPhysicalExam')}</h2>
                                <ul>
                                  {pathologyLines(aiResult.exameFisico).map((item, index) => (
                                    <li key={`print-exam-${index}`}>{normalizeSharedText(item)}</li>
                                  ))}
                                </ul>
                              </section>
                            )}

                            {pathologyText(aiResult.conduta) && (
                              <section className="consult-pathology-print-card print-no-break">
                                <h2>{t('consultSuggestedConduct')}</h2>
                                <p>{normalizeSharedText(pathologyText(aiResult.conduta))}</p>
                              </section>
                            )}

                            {pathologyLines(aiResult.opme).length > 0 && (
                              <section className="consult-pathology-print-card print-no-break">
                                <h2>{t('consultOpmeSuggestion')}</h2>
                                <ul>
                                  {pathologyLines(aiResult.opme).map((item, index) => (
                                    <li key={`print-opme-${index}`}>{normalizeSharedText(item)}</li>
                                  ))}
                                </ul>
                              </section>
                            )}

                            {pathologyAiCards.length > 0 && (
                              <section className="consult-pathology-print-card">
                                <h2>{t('consultPathologyPrintEvidence')}</h2>
                                <div className="consult-pathology-print-subsections">
                                  {pathologyAiCards
                                    .filter((card) => Boolean(cleanMedicalMarkdownText(card.value)))
                                    .map((card) => (
                                      <article key={`print-${card.key}`} className="consult-pathology-print-subsection print-no-break">
                                        <h3>{card.title}</h3>
                                        <p>{normalizeSharedText(normalizeMedicalMarkdownInput(card.value))}</p>
                                      </article>
                                    ))}
                                </div>
                              </section>
                            )}
                          </main>

                          <footer className="consult-pathology-print-footer print-no-break">
                            {t('consultClinicalSupportDocumentDisclaimer')}
                          </footer>
                        </div>
                      </div>,
                      document.body
                    )}
                    </div>
                ) : (
                    // EMPTY STATE SEARCH
                    <div className="flex min-h-[520px] flex-col items-center justify-center gap-5 text-[color:var(--op-text-muted)]">
                    <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[color:var(--op-border-strong)] bg-[radial-gradient(circle,_rgba(34,211,238,0.08),_rgba(2,6,23,0.92))] shadow-[0_0_30px_rgba(34,211,238,0.08)]">
                        <BookOpen className="h-10 w-10 text-cyan-300" />
                    </div>
                    <div className="space-y-2 text-center">
                        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('consultPathologyEmptyKicker')}</p>
                        <p className="text-sm font-semibold text-[color:var(--op-text)]">{t('consultPathologyEmptyTitle')}</p>
                        <div className="mx-auto grid max-w-[520px] grid-cols-1 gap-3 pt-2 sm:grid-cols-3">
                            {[
                                t('consultPathologyTagAoOta'),
                                t('consultPathologyTagConductMaterials'),
                                t('consultPathologyTagGuidelinesEvidence'),
                            ].map((label) => (
                                <div key={label} className="rounded-2xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-4 py-3 text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--op-text-muted)] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                    {label}
                                </div>
                            ))}
                        </div>
                    </div>
                    </div>
                )}
            </>
          ) : (
            // REPORT ANALYSIS MODE
            <PremiumPageShell className="space-y-8 animate-in fade-in pb-10">
                <div className="rounded-[28px] border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-6 py-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_18px_45px_rgba(0,0,0,0.22)]">
                    <div className="flex items-start gap-4">
                        <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[color:var(--op-border-strong)] bg-[radial-gradient(circle,_rgba(34,211,238,0.12),_rgba(2,6,23,0.92))] shadow-[0_0_26px_rgba(34,211,238,0.1)]">
                            <ScanText className="h-6 w-6 text-cyan-300" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-cyan-300">{t('reportAnalysisKicker')}</p>
                            <h2 className="mt-1 text-2xl font-bold text-[color:var(--op-text)]">{t('reportAnalysisTitle')}</h2>
                            <p className="mt-1 text-sm text-[color:var(--op-text-muted)]">{t('reportAnalysisSubtitle')}</p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 gap-8 xl:grid-cols-[0.9fr_1.4fr]">
                    {/* Left: Upload */}
                    <div className="xl:col-span-1 flex w-full justify-center">
                        <div className="laudo-input-section flex w-full max-w-[600px] flex-col items-center">
                            <div className="laudo-input-content w-full space-y-4">
                            <div className="glass rounded-[24px] border border-[color:var(--op-border)] p-5 shadow-2xl">
                                <div className="text-left">
                                    <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-cyan-300">{t('consultReportInputKicker')}</p>
                                    <p className="mt-1 text-sm text-[color:var(--op-text-muted)]">{t('consultReportInputSubtitle')}</p>
                                </div>
                            </div>
                            <div 
                                className={`group relative flex w-full cursor-pointer flex-col overflow-hidden rounded-[28px] border-2 border-dashed text-center transition-all duration-200 ${reportImage ? 'border-cyan-400/50 bg-[color:var(--op-bg-deep)]' : 'border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] hover:-translate-y-0.5 hover:border-[color:var(--op-border-strong)] hover:bg-white/[0.03] hover:shadow-[0_0_0_1px_rgba(34,211,238,0.08),0_0_40px_rgba(34,211,238,0.08)]'}`}
                                style={{
                                    minHeight: reportImage ? 'clamp(420px, 56vh, 520px)' : '260px',
                                    height: reportImage ? 'clamp(420px, 56vh, 520px)' : '260px',
                                    maxHeight: reportImage ? 'clamp(420px, 56vh, 520px)' : '260px',
                                }}
                                onClick={() => !reportImage && reportInputRef.current?.click()}
                            >
                                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(34,211,238,0.07),transparent_52%)] opacity-80" />
                                {reportImage ? (
                                    <>
                                        <div className="relative z-10 flex h-full w-full flex-1 items-center justify-center overflow-hidden p-3 sm:p-4">
                                            {/* Handle PDF Display */}
                                            {reportImage.startsWith('data:application/pdf') ? (
                                                <div className="flex h-full w-full items-stretch justify-stretch overflow-hidden rounded-[22px] border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]">
                                                    <iframe
                                                        src={reportImage}
                                                        title={t('consultPdfDocument')}
                                                        className="h-full w-full border-0"
                                                    />
                                                </div>
                                            ) : (
                                                <img
                                                    src={reportImage}
                                                    alt="Laudo"
                                                    className="max-h-full max-w-full object-contain"
                                                    style={{ objectFit: 'contain' }}
                                                />
                                            )}
                                        </div>
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); setReportImage(null); setReportResult(null); }}
                                            className="absolute right-3 top-3 z-20 rounded-full border border-red-500/30 bg-red-500/20 p-2 text-white shadow-lg transition-colors hover:bg-red-500/30"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </>
                                ) : (
                                    <div className="relative z-10 flex h-full w-full flex-col items-center justify-center gap-4 text-center pointer-events-none">
                                        <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full border border-[color:var(--op-border-strong)] bg-[color:var(--op-card-bg-soft)] shadow-[0_0_28px_rgba(34,211,238,0.1)]">
                                            <FileText className="h-10 w-10 text-cyan-300" />
                                        </div>
                                        <div className="space-y-1.5">
                                            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[color:var(--op-text)]">{t('consultUploadReport')}</p>
                                            <p className="text-xs text-[color:var(--op-text-muted)]" style={{ opacity: 0.82 }}>{t('consultImageOrPdf')}</p>
                                        </div>
                                    </div>
                                )}
                                <input 
                                    type="file" 
                                    ref={reportInputRef} 
                                    onChange={handleReportUpload} 
                                    className="hidden" 
                                    accept="image/*,.pdf"
                                    capture="environment"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowPatientFilePicker(true)}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-4 py-3 text-xs font-bold uppercase tracking-widest text-[color:var(--op-text)] transition-all duration-200 hover:-translate-y-0.5 hover:border-[color:var(--op-border-strong)] hover:bg-white/[0.04] hover:shadow-[0_10px_30px_rgba(34,211,238,0.08)]"
                            >
                                <FolderSearch className="h-4 w-4 text-cyan-300" />
                                {t('choosePatientFile')}
                            </button>
                            <button 
                                onClick={analyzeReport}
                                disabled={!reportImage || isAnalyzing}
                                className="w-full rounded-[18px] bg-gradient-to-r from-cyan-500 via-sky-500 to-blue-500 py-4 font-bold text-white shadow-[0_12px_32px_rgba(34,211,238,0.18)] transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:-translate-y-0.5 hover:shadow-[0_18px_42px_rgba(34,211,238,0.22)]"
                            >
                                {isAnalyzing ? <Loader2 className="h-5 w-5 animate-spin" /> : <ScanText className="h-5 w-5" />}
                                {isAnalyzing ? t('consultReadingReport') : t('consultAnalyzeConduct')}
                            </button>
                        </div>
                    </div>
                    </div>

                    {/* Right: Analysis Result */}
                    {/* ... Analysis Result unchanged ... */}
                    <div className="xl:col-span-2">
                        <div className="glass h-full min-h-[620px] rounded-[28px] border border-[color:var(--op-border)] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.45)]">
                        <div className="mb-4 flex items-center justify-between border-b border-white/5 pb-3">
                            <div>
                                <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-cyan-300">{t('consultResultTitle')}</p>
                                <p className="mt-1 text-sm text-[color:var(--op-text-muted)]">{t('consultWaitingReportSubtitle')}</p>
                            </div>
                        </div>
                        {isAnalyzing ? (
                            <div className="flex h-full min-h-[560px] flex-col items-center justify-center gap-5 text-[color:var(--op-text-muted)] animate-in fade-in">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-cyan-400/20 bg-cyan-500/10 shadow-[0_0_28px_rgba(34,211,238,0.12)]">
                                  <Loader2 className="h-10 w-10 animate-spin text-cyan-300" />
                                </div>
                                <div className="space-y-2 text-center">
                                  <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('consultReadingReport')}</p>
                                  <p className="text-sm font-semibold text-[color:var(--op-text)]">{reportLoadingMessages[reportLoadingIndex]}</p>
                                  <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[color:var(--op-text-muted)]">
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/80 animate-pulse" />
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/60 animate-pulse [animation-delay:150ms]" />
                                    <span className="h-1.5 w-1.5 rounded-full bg-cyan-300/40 animate-pulse [animation-delay:300ms]" />
                                  </div>
                                </div>
                              </div>
                        ) : reportResult ? (
                            <div className="space-y-6 animate-in slide-in-from-right-4">
                                {/* Header / Diagnosis */}
                                <div className="flex items-start justify-between border-b border-white/5 pb-4">
                                    <div>
                                <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('consultResultTitle')}</p>
                                        <h2 className="mt-2 text-3xl font-bold text-[color:var(--op-text)]">{reportResult.diagnosis}</h2>
                                        <div className="mt-3 flex gap-2">
                                            <span className={`rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] ${
                                                reportResult.category === 'Cirúrgico' || reportResult.category === 'Surgical' 
                                                ? 'border-red-500/30 bg-red-500/10 text-red-300' 
                                                : reportResult.category === 'Conservador' || reportResult.category === 'Conservative'
                                                ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
                                                : 'border-amber-500/30 bg-amber-500/10 text-amber-300'
                                            }`}>
                                                {reportResult.category}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-2">
                                        <button 
                                            onClick={handleAddReportToPatientRecord}
                                            disabled={isSavingReportToPatient || !selectedReportPatient || reportPatientSaved}
                                            className={`flex items-center gap-2 rounded-xl border px-4 py-2 text-xs font-bold uppercase tracking-wide transition-all ${
                                              reportPatientSaved
                                                ? 'border-emerald-500/50 bg-emerald-500/15 text-emerald-300'
                                                : 'border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] text-[color:var(--op-text)] hover:border-[color:var(--op-border-strong)] hover:bg-white/[0.04] disabled:opacity-50 disabled:cursor-not-allowed'
                                            }`}
                                        >
                                            {isSavingReportToPatient ? <Loader2 className="h-4 w-4 animate-spin" /> : reportPatientSaved ? <Check className="h-4 w-4" /> : <FilePlus className="h-4 w-4" />}
                                            {isSavingReportToPatient
                                              ? t('consultAddingToRecord')
                                              : reportPatientSaved
                                                ? t('consultAddedToRecord')
                                                : t('consultAddToRecord')}
                                        </button>
                                        {selectedReportPatient && (
                                            <span className="text-[10px] text-[color:var(--op-text-muted)]">
                                                {t('consultSelectPatientTitle')}: {selectedReportPatient.name}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                <div className="rounded-2xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                    <div className="flex items-center justify-between gap-3 mb-2">
                                        <h4 className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--op-text-muted)]">
                                            <BookOpen className="h-3 w-3 text-cyan-300" /> {t('consultSelectPatientTitle')}
                                        </h4>
                                        {selectedReportPatient && (
                                            <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                                                {selectedReportPatient.name}
                                            </span>
                                        )}
                                    </div>
                                    <p className="mb-3 text-xs text-[color:var(--op-text-muted)]">{t('consultSelectPatientSubtitle')}</p>
                                    {patients.length > 0 ? (
                                            <select
                                                value={selectedReportPatientId || selectedReportPatient?.id || ''}
                                                onChange={(e) => {
                                                setSelectedReportPatientId(e.target.value);
                                                setReportPatientError('');
                                                setReportPatientSaved(false);
                                                setGeneratedDocument(null);
                                                setDocumentError('');
                                            }}
                                            className="w-full rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/80 px-4 py-3 text-sm text-[color:var(--op-text)] outline-none transition-colors focus:border-[color:var(--op-border-strong)]"
                                        >
                                            <option value="">{t('consultSelectPatientPlaceholder')}</option>
                                            {patients.map((patient) => (
                                                <option key={patient.id} value={patient.id}>
                                                    {patient.name} - {patient.anatomy}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <p className="text-xs text-amber-400">{t('consultNoPatientsAvailable')}</p>
                                    )}
                                    {reportPatientError && (
                                        <p className="mt-2 text-xs text-red-400">{reportPatientError}</p>
                                    )}
                                </div>

                                {/* Findings */}
                                <div className="rounded-2xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-4">
                                    <h4 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--op-text-muted)]">
                                        <Search className="h-3 w-3 text-cyan-300" /> {t('consultRelevantFindings')}
                                    </h4>
                                    <ul className="space-y-1">
                                        {reportResult.findings?.map((f: string, i: number) => (
                                            <li key={i} className="flex items-start gap-2 text-sm text-[color:var(--op-text)]">
                                                <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-cyan-300" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Reasoning */}
                                <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/8 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                    <h4 className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">
                                        <BrainCircuit className="h-3 w-3" /> {t('consultClinicalReasoning')}
                                    </h4>
                                    <p className="text-sm leading-relaxed text-[color:var(--op-text)]">{reportResult.reasoning}</p>
                                </div>

                                {/* Plan */}
                                <div className="rounded-2xl border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04)]">
                                    <h4 className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[color:var(--op-text-muted)]">
                                        <Stethoscope className="h-3 w-3 text-cyan-300" /> {t('consultDetailedPlan')}
                                    </h4>
                                    {/* UPDATED: Use Markdown Renderer */}
                                    <div className="text-sm leading-relaxed text-[color:var(--op-text)]">
                                        {renderInlineMedicalMarkdown(reportResult.treatmentPlan, 'text-sm leading-7 text-slate-300')}
                                    </div>
                                    
                                    {reportResult.rehabProtocol && (
                                        <div className="mt-4 border-t border-white/5 pt-4">
                                            <span className="text-xs font-bold uppercase text-[color:var(--op-text-muted)]">{t('consultRehabilitation')}</span>
                                            <p className="mt-1 text-sm text-[color:var(--op-text-muted)]">{reportResult.rehabProtocol}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Medical Prescription (New Section) */}
                                {reportResult.prescription && (
                                    <div className="relative overflow-hidden rounded-[24px] border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/80 p-6 shadow-[0_18px_45px_rgba(0,0,0,0.28)]">
                                        <div className="mb-4 flex items-center justify-between border-b border-white/5 pb-2">
                                            <h4 className="flex items-center gap-2 text-sm font-bold uppercase text-[color:var(--op-text)]">
                                                <Pill className="h-4 w-4 text-emerald-300" /> {t('consultPrescriptionGuide')}
                                            </h4>
                                            <button 
                                                onClick={handleCopyPrescription}
                                                className="rounded p-1.5 text-[color:var(--op-text-muted)] transition-colors hover:bg-white/5 hover:text-[color:var(--op-text)]"
                                                title={t('consultCopyPrescription')}
                                            >
                                                {copyPrescription ? <Check className="h-4 w-4 text-emerald-400"/> : <Copy className="h-4 w-4"/>}
                                            </button>
                                        </div>
                                        
                                        <div className="space-y-6">
                                            <div>
                                                <p className="mb-2 text-xs font-bold uppercase text-[color:var(--op-text-muted)]">{t('consultOralUse')}</p>
                                                <ul className="space-y-3">
                                                    {reportResult.prescription.medications?.map((med: any, i: number) => {
                                                        const hasConflict = checkAllergyConflict(med.name);
                                                        return (
                                                            <li key={i} className={`flex flex-col border-b border-dashed border-white/10 pb-2 last:border-0 ${hasConflict ? 'rounded-lg border border-red-400/20 bg-red-500/10 p-2' : ''}`}>
                                                                <div className="flex items-center justify-between">
                                                                    <span className={`text-sm font-bold ${hasConflict ? 'text-red-300' : 'text-[color:var(--op-text)]'}`}>{med.name}</span>
                                                                    {hasConflict && (
                                                                        <span className="flex items-center gap-1 rounded-full border border-red-400/30 bg-red-500/10 px-2 py-0.5 text-[10px] font-bold text-red-300">
                                                                            <AlertTriangle className="w-3 h-3" /> {t('consultAllergyDetected')}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span className={`text-xs italic ${hasConflict ? 'text-red-200/90' : 'text-[color:var(--op-text-muted)]'}`}>{med.instructions}</span>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                            
                                            {reportResult.prescription.careInstructions && (
                                                <div className="rounded-lg border border-cyan-400/15 bg-cyan-500/8 p-3">
                                                    <p className="mb-1 flex items-center gap-1 text-xs font-bold uppercase text-cyan-300">
                                                        <ClipboardList className="w-3 h-3" /> {t('consultGeneralGuidance')}
                                                    </p>
                                                    <ul className="list-disc space-y-1 pl-4 text-xs text-[color:var(--op-text)]">
                                                        {reportResult.prescription.careInstructions.map((inst: string, i: number) => (
                                                            <li key={i}>{inst}</li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                        
                                        <div className="mt-4 border-t border-white/5 pt-2 text-center text-[10px] italic text-[color:var(--op-text-muted)]">
                                            {t('consultPrescriptionDisclaimer')}
                                        </div>
                                    </div>
                                )}

                                <div className="rounded-2xl border border-emerald-400/18 bg-gradient-to-br from-slate-950 via-cyan-950/35 to-emerald-950/30 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.04),0_18px_40px_rgba(0,0,0,0.18)]">
                                    <div className="mb-4 flex flex-col gap-1">
                                        <div className="flex items-center gap-2">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-emerald-400/20 bg-emerald-400/10 text-emerald-300 shadow-[0_0_18px_rgba(16,185,129,0.12)]">
                                                <FileText className="h-4 w-4" />
                                            </div>
                                            <h4 className="text-sm font-semibold uppercase tracking-[0.16em] text-emerald-300">
                                                {t('consultComplementaryDocumentsTitle')}
                                            </h4>
                                        </div>
                                        <p className="text-xs text-[color:var(--op-text-muted)]" style={{ opacity: 0.85 }}>
                                            Próximo passo: gerar documentos clínicos a partir da análise.
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        {reportDocumentOptions.map((option) => {
                                            const active = selectedReportDocumentType === option.type;
                                            const Icon = option.icon;
                                            return (
                                                <button
                                                    key={option.type}
                                                    type="button"
                                                    onClick={() => handleSelectReportDocument(option.type)}
                                                    className={`group relative rounded-2xl border p-4 text-left transition-all duration-200 active:scale-[0.98] cursor-pointer ${
                                                        active
                                                            ? 'border-emerald-400/45 bg-gradient-to-br from-emerald-500/18 via-teal-500/12 to-cyan-500/12 text-white shadow-[0_16px_40px_rgba(16,185,129,0.18)] -translate-y-0.5'
                                                            : 'border-emerald-400/15 bg-[color:var(--op-bg-deep)]/75 text-emerald-50/78 hover:-translate-y-0.5 hover:border-emerald-300/30 hover:bg-gradient-to-br hover:from-emerald-500/12 hover:via-teal-500/10 hover:to-cyan-500/10 hover:text-white hover:shadow-[0_14px_34px_rgba(16,185,129,0.12)]'
                                                    }`}
                                                >
                                                    <div className="flex items-start gap-3">
                                                        <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border transition-all ${
                                                            active
                                                              ? 'border-emerald-300/35 bg-emerald-400/12 text-emerald-200 shadow-[0_0_20px_rgba(16,185,129,0.14)]'
                                                              : 'border-emerald-400/15 bg-white/[0.03] text-emerald-200/85 group-hover:border-emerald-300/30 group-hover:bg-emerald-400/10 group-hover:text-emerald-100'
                                                        }`}>
                                                            <Icon className="h-5 w-5" />
                                                        </div>
                                                        <div className="min-w-0 flex-1 space-y-1">
                                                            <div className="flex items-center gap-2">
                                                                <p className={`text-[13px] font-semibold leading-tight ${active ? 'text-white' : 'text-[color:var(--op-text)]'}`}>
                                                                    {t(option.labelKey)}
                                                                </p>
                                                                {option.featured && (
                                                                    <span className="inline-flex shrink-0 items-center rounded-full border border-emerald-300/25 bg-emerald-400/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.22em] text-emerald-200">
                                                                        Mais usado
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className={`text-[11px] leading-snug ${active ? 'text-white/80' : 'text-[color:var(--op-text-muted)]/70'}`}>
                                                                Gerar com base na análise concluída.
                                                            </p>
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {selectedReportDocumentType && (
                                        <div className="mt-4 space-y-3 rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/80 p-4">
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="text-xs font-semibold uppercase tracking-widest text-emerald-200/85">
                                                    {selectedReportDocumentLabel}
                                                </p>
                                            </div>

                                            <div className="space-y-2">
                                                <label className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--op-text-muted)]">
                                                    {t('consultDocumentClinicalOpinionLabel')}
                                                </label>
                                                <textarea
                                                    value={documentClinicalOpinion}
                                                    onChange={(e) => setDocumentClinicalOpinion(e.target.value)}
                                                    placeholder={t('consultDocumentClinicalOpinionPlaceholder')}
                                                    className="min-h-[110px] w-full rounded-xl border border-[color:var(--op-border)] bg-[color:var(--op-bg-deep)]/80 px-4 py-3 text-sm text-[color:var(--op-text)] placeholder:text-[color:var(--op-text-muted)]/60 outline-none transition-colors focus:border-[color:var(--op-border-strong)]"
                                                />
                                            </div>

                                    <div className="flex flex-wrap items-center gap-3">
                                        <button
                                            type="button"
                                            onClick={handleGenerateReportDocument}
                                            disabled={isGeneratingDocument || !selectedReportPatient}
                                            className="inline-flex items-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-xs font-semibold uppercase tracking-widest text-emerald-100 transition-all hover:-translate-y-0.5 hover:border-emerald-300/50 hover:bg-gradient-to-r hover:from-emerald-500/20 hover:via-teal-500/18 hover:to-cyan-500/18 hover:shadow-[0_14px_34px_rgba(16,185,129,0.16)] disabled:cursor-not-allowed disabled:opacity-60"
                                        >
                                            {isGeneratingDocument ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                                            {isGeneratingDocument ? t('consultGeneratingDocument') : t('consultGenerateDocument')}
                                        </button>
                                        {!selectedReportPatient && (
                                            <span className="text-xs text-amber-300">{t('consultSelectPatientRequired')}</span>
                                        )}
                                        {documentError && (
                                            <span className="text-xs text-red-400">{documentError}</span>
                                        )}
                                            </div>
                                        </div>
                                    )}

                                    {generatedDocument && (
                                        <div className="mt-4 rounded-2xl border border-emerald-500/20 bg-[color:var(--op-bg-deep)]/80 p-5 shadow-[0_18px_45px_rgba(0,0,0,0.28)]">
                                            <div className="mb-4 flex items-start justify-between gap-3 border-b border-white/5 pb-3">
                                                <div>
                                                    <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-300">
                                                        {t('consultGeneratedDocumentTitle')}
                                                    </p>
                                                    <h4 className="mt-1 text-lg font-bold text-[color:var(--op-text)]">{generatedDocument.title}</h4>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleCopyGeneratedDocument}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-[color:var(--op-border)] bg-[color:var(--op-card-bg-soft)] px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-[color:var(--op-text-muted)] transition-colors hover:border-[color:var(--op-border-strong)] hover:bg-white/[0.04] hover:text-[color:var(--op-text)]"
                                                >
                                                    {copyDocument ? <Check className="h-4 w-4 text-emerald-400" /> : <Copy className="h-4 w-4" />}
                                                    {copyDocument ? t('consultCopied') : t('consultCopyDocument')}
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={handleExportProfessionalPdf}
                                                    disabled={isExportingPdf}
                                                    className="inline-flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-amber-200 transition-colors hover:bg-amber-500/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                                                >
                                                    {isExportingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                                                    {isExportingPdf ? t('consultExportingPdf') : t('consultExportPdfProfessional')}
                                                </button>
                                            </div>
                                            {pdfExportError && (
                                                <p className="mb-3 text-xs text-red-400">{pdfExportError}</p>
                                            )}
                                            <div className="space-y-2">
                                                <div className="space-y-2">
                                                    {renderInlineMedicalMarkdown(normalizedGeneratedDocumentContent, 'text-sm leading-7 text-slate-300')}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div className="flex h-full min-h-[560px] flex-col items-center justify-center gap-5 text-[color:var(--op-text-muted)] animate-in fade-in">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full border border-[color:var(--op-border-strong)] bg-[radial-gradient(circle,_rgba(34,211,238,0.08),_rgba(2,6,23,0.92))] shadow-[0_0_30px_rgba(34,211,238,0.08)]">
                                    <FileText className="h-10 w-10 text-cyan-300" />
                                </div>
                                <div className="space-y-2 text-center">
                                    <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-cyan-300">{t('consultWaitingReport')}</p>
                                    <p className="text-sm font-semibold text-[color:var(--op-text)]">{t('consultWaitingReportSubtitle')}</p>
                                    <p className="max-w-[360px] text-xs leading-relaxed text-[color:var(--op-text-muted)]">
                                        {t('consultWaitingReportAction')}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            </PremiumPageShell>
          )}

          <PatientFilePickerModal
            open={showPatientFilePicker}
            title={t('choosePatientFile')}
            subtitle={t('choosePatientFileSubtitle')}
            initialPatientId={selectedReportPatientId || activePatient?.id || ''}
            allowPdf
            onClose={() => setShowPatientFilePicker(false)}
            onSelectFile={async ({ patient, dataUrl }) => {
              setSelectedReportPatientId(patient.id);
              setReportImage(dataUrl);
              setReportResult(null);
              setSelectedReportDocumentType(null);
              setGeneratedDocument(null);
              setDocumentError('');
              setReportPatientError('');
              setReportPatientSaved(false);
            }}
          />

        </div>
      </div>
    </div>
  );
};

export default ConsultModule;

